import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import tourAssistantIcon from '@/static/icons/logobsf_new.svg';
import {
  ENABLE_SAMPLES_REGISTER_GUIDED_TOUR,
  IS_LOCAL_TEST,
  SAMPLES_REGISTER_GUIDED_TOUR_MAX_AUTO_SHOWS_WITHOUT_COMPLETION,
  SAMPLES_REGISTER_GUIDED_TOUR_MAX_IN_PROGRESS_AUTO_RESHOWS,
  SAMPLES_REGISTER_GUIDED_TOUR_RESHOW_INTERVAL_DAYS,
  SAMPLES_REGISTER_GUIDED_TOUR_STORAGE_KEY,
  SAMPLES_REGISTER_TEMPLATES_GUIDED_TOUR_STORAGE_KEY,
} from '@/js/constants';

const TOUR_WAIT_TIMEOUT = 15000;
const TOUR_WAIT_INTERVAL = 1000;
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const TOUR_SKIP_BUTTON_CLASS = 'samples-register-tour-skip-btn';
const TOUR_POPOVER_CLASS = 'samples-register-tour-popover';
const TOUR_POPOVER_STYLES_ID = 'samples-register-tour-popover-styles';
const TOUR_POPOVER_WITH_IMAGE_CLASS =
  'samples-register-tour-popover--with-image';
const TOUR_SMALL_IMAGE_POPOVER_CLASS =
  'samples-register-tour-popover--small-image';
const TOUR_INVOICE_DETAILS_COMPACT_POPOVER_CLASS =
  'samples-register-tour-popover--invoice-details-compact';
const TOUR_DASHBOARD_POPOVER_CLASS =
  'samples-register-tour-popover--dashboard-intro';
const DASHBOARD_REGISTER_BUTTON_SELECTOR =
  '[data-tour-id="samples-register-dashboard-button"]';
const DASHBOARD_TEMPLATES_BUTTON_SELECTOR =
  '[data-tour-id="samples-register-templates-dashboard-button"]';
const EXPRESS_REGISTER_TEMPLATE_SAVE_BUTTON_SELECTOR =
  '[data-tour-id="samples-register-express-save-template-button"]';
const TEMPLATE_CREATE_PAGE_ROOT_SELECTOR =
  '[data-tour-id="samples-register-template-create-page"]';
const TEMPLATE_CREATE_FORM_SELECTOR =
  '[data-tour-id="samples-register-template-create-form"]';
const TEMPLATES_LIST_PAGE_ROOT_SELECTOR =
  '[data-tour-id="samples-register-templates-list-page"]';
const TEMPLATES_LIST_GRID_SELECTOR =
  '[data-tour-id="samples-register-templates-list-grid"]';
const TEMPLATE_DETAILS_PAGE_ROOT_SELECTOR =
  '[data-tour-id="samples-register-template-details-page"]';
const TEMPLATE_DETAILS_SUMMARY_SELECTOR =
  '[data-tour-id="samples-register-template-details-summary"]';
const TEMPLATE_DETAILS_ACTIONS_SELECTOR =
  '[data-tour-id="samples-register-template-details-actions"]';
const TEMPLATE_DETAILS_LOAD_BUTTON_SELECTOR =
  '[data-tour-id="samples-register-template-details-load-button"]';
const TEMPLATE_DETAILS_EDIT_BUTTON_SELECTOR =
  '[data-tour-id="samples-register-template-details-edit-button"]';
const TEMPLATE_SAMPLES_GRID_SELECTOR =
  '[data-tour-id="samples-register-template-samples-grid"]';
const TEMPLATE_SAMPLES_EDIT_BUTTON_SELECTOR =
  '.samples-register-template-samples-edit-button';
const INVOICE_LIST_STATUS_FILTER_SELECTORS = [
  '[data-tour-id="samples-list-status-filter-pending"]',
  '[data-tour-id="samples-list-status-filter-received"]',
  '[data-tour-id="samples-list-status-filter-in-transit"]',
];
const INVOICE_LIST_STATUS_FILTERS_TOUR_TARGET_ID =
  'samples-register-tour-invoice-list-filters-target';
const TOUR_ASSET_CONTEXTS = {
  img: require.context(
    '@/static/img',
    false,
    /\.(png|jpe?g|webp|svg)$/
  ),
  icons: require.context(
    '@/static/icons',
    false,
    /\.(png|jpe?g|webp|svg)$/
  ),
};
const TOUR_IMAGE_FILE_PATTERN = /\.(png|jpe?g|webp|gif|svg)(\?.*)?$/i;
const TOUR_STEP_IMAGE_FALLBACKS = {
  expressGrid: 'img/MuestrasExpressBesafer 4.0.png',
  confirmationPanel: 'img/MuestrasRegistradasBesafer 4.0.png',
  invoiceListIntro: 'img/ListadoAlbaranesBesafer4.0.png',
  invoiceManagement: 'img/ListadoAlbaranesBesafer4.0.png',
  invoiceDetailFromList: 'img/BotonesAlbaranBesafer 4.0.png',
  invoiceDetails: 'img/DetallesMuestrasBesafer4.0.png',
  templatesListIntro: 'img/ListadoPlantillas-ES.png',
  templateDetailsIntro: 'img/DetallesPlantillas-ES.png',
  templateDetailsSummary: 'img/CabeceraPlantillas-ES.png',
  templateDetailsSamplesEdit: 'img/IconoEditar.png',
};
const SAMPLES_REGISTER_TOUR_PENDING_START_KEY =
  'samples-register-tour-pending-start';
const SAMPLES_REGISTER_TOUR_PENDING_INVOICE_LIST_START_KEY =
  'samples-register-tour-pending-invoice-list-start';
const SAMPLES_REGISTER_TOUR_PENDING_INVOICE_LIST_DETAILS_START_KEY =
  'samples-register-tour-pending-invoice-list-details-start';
const SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_CREATE_START_KEY =
  'samples-register-tour-pending-template-create-start';
const SAMPLES_REGISTER_TOUR_PENDING_TEMPLATES_LIST_START_KEY =
  'samples-register-tour-pending-templates-list-start';
const SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_DETAILS_START_KEY =
  'samples-register-tour-pending-template-details-start';
const SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_RESUME_START_KEY =
  'samples-register-tour-pending-template-resume-start';
const EXPRESS_REGISTER_GRID_SELECTOR =
  '[data-tour-id="samples-register-express-grid"]';
const TEMPLATE_SAVE_BUTTON_TOUR_TARGET_ID =
  'samples-register-tour-template-save-button-target';
let activeDriver = null;
let activeDriverOwnerVm = null;
let autoAdvanceTimeoutId = null;
let lockedScrollY = 0;
let previousHtmlOverflow = '';
let previousBodyOverflow = '';
let previousBodyPosition = '';
let previousBodyTop = '';
let previousBodyWidth = '';
let lockedScrollableElements = [];
const TOUR_CLICK_BLOCK_EVENTS = [
  'click',
  'dblclick',
  'mousedown',
  'mouseup',
  'pointerdown',
  'pointerup',
  'touchstart',
  'touchend',
];

const TOUR_SCROLLABLE_SELECTORS = [
  '.view-main',
  '.views',
  '.page',
  '.page-content',
  '.safe-areas',
];
const TEMPLATES_TOUR_VIDEO_NAME = 'plantillas muestreo';

const SCROLL_BLOCK_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
  'Spacebar',
]);

function clearAutoAdvanceTimeout() {
  if (autoAdvanceTimeoutId) {
    clearTimeout(autoAdvanceTimeoutId);
    autoAdvanceTimeoutId = null;
  }
}

function preventTourScroll(event) {
  event.preventDefault();
}

function preventTourScrollKeys(event) {
  if (SCROLL_BLOCK_KEYS.has(event.key)) {
    event.preventDefault();
  }
}

function preventClicksOutsideTour(event) {
  if (!event.isTrusted) {
    return;
  }

  const targetElement =
    event.target instanceof Element
      ? event.target
      : event.target?.parentElement;

  if (targetElement?.closest?.('.driver-popover')) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();
}

function lockTourScroll() {
  const html = document.documentElement;
  const body = document.body;

  if (
    !html ||
    !body ||
    body.dataset.samplesRegisterTourScrollLocked === 'true'
  ) {
    return;
  }

  lockedScrollY = window.scrollY || window.pageYOffset || 0;
  previousHtmlOverflow = html.style.overflow;
  previousBodyOverflow = body.style.overflow;
  previousBodyPosition = body.style.position;
  previousBodyTop = body.style.top;
  previousBodyWidth = body.style.width;

  html.style.overflow = 'hidden';
  body.style.overflow = 'hidden';
  body.style.position = 'fixed';
  body.style.top = `-${lockedScrollY}px`;
  body.style.width = '100%';
  body.dataset.samplesRegisterTourScrollLocked = 'true';

  lockedScrollableElements = Array.from(
    document.querySelectorAll(TOUR_SCROLLABLE_SELECTORS.join(','))
  ).map((element) => {
    const previousOverflow = element.style.overflow;
    const previousOverflowY = element.style.overflowY;
    const previousTouchAction = element.style.touchAction;

    element.style.overflow = 'hidden';
    element.style.overflowY = 'hidden';
    element.style.touchAction = 'none';

    return {
      element,
      previousOverflow,
      previousOverflowY,
      previousTouchAction,
    };
  });

  window.addEventListener('wheel', preventTourScroll, {
    passive: false,
  });
  window.addEventListener('touchmove', preventTourScroll, {
    passive: false,
  });
  window.addEventListener('keydown', preventTourScrollKeys, {
    passive: false,
  });
  TOUR_CLICK_BLOCK_EVENTS.forEach((eventName) => {
    document.addEventListener(eventName, preventClicksOutsideTour, {
      capture: true,
      passive: false,
    });
  });
}

function unlockTourScroll() {
  const html = document.documentElement;
  const body = document.body;

  if (
    !html ||
    !body ||
    body.dataset.samplesRegisterTourScrollLocked !== 'true'
  ) {
    return;
  }

  html.style.overflow = previousHtmlOverflow;
  body.style.overflow = previousBodyOverflow;
  body.style.position = previousBodyPosition;
  body.style.top = previousBodyTop;
  body.style.width = previousBodyWidth;
  delete body.dataset.samplesRegisterTourScrollLocked;
  lockedScrollableElements.forEach(
    ({
      element,
      previousOverflow,
      previousOverflowY,
      previousTouchAction,
    }) => {
      element.style.overflow = previousOverflow;
      element.style.overflowY = previousOverflowY;
      element.style.touchAction = previousTouchAction;
    }
  );
  lockedScrollableElements = [];
  window.removeEventListener('wheel', preventTourScroll);
  window.removeEventListener('touchmove', preventTourScroll);
  window.removeEventListener('keydown', preventTourScrollKeys);
  TOUR_CLICK_BLOCK_EVENTS.forEach((eventName) => {
    document.removeEventListener(eventName, preventClicksOutsideTour, {
      capture: true,
    });
  });
  window.scrollTo(0, lockedScrollY);
}

function resolveTourUser(store) {
  const userState = store?.state?.user || {};
  const authenticatedUser = userState?.authenticatedUser || {};
  const candidates = [
    authenticatedUser?.username,
    authenticatedUser?.email,
    userState?.userName,
    userState?.username,
  ];

  const resolved = candidates.find(
    (value) => typeof value === 'string' && value.trim() !== ''
  );

  return resolved ? resolved.trim() : 'anonymous';
}

function getStorageKey(
  store,
  storageKeyOverride = SAMPLES_REGISTER_GUIDED_TOUR_STORAGE_KEY
) {
  return `${storageKeyOverride}:${resolveTourUser(store)}`;
}

function getCurrentVideoName(store) {
  const videoPeriodState = store?.state?.videoPeriodModule || {};
  return typeof videoPeriodState.currentVideo?.name === 'string'
    ? videoPeriodState.currentVideo.name.trim()
    : '';
}

function isTemplatesGuidedTourVideoActive(store) {
  return (
    getCurrentVideoName(store).toLowerCase() ===
    TEMPLATES_TOUR_VIDEO_NAME
  );
}

function getBackendTourPeriodKey(
  store,
  storageKeyOverride = SAMPLES_REGISTER_GUIDED_TOUR_STORAGE_KEY
) {
  const videoPeriodState = store?.state?.videoPeriodModule || {};
  const isTemplatesTour =
    storageKeyOverride ===
    SAMPLES_REGISTER_TEMPLATES_GUIDED_TOUR_STORAGE_KEY;

  if (
    (!isTemplatesTour && !videoPeriodState.isRegistroMuestras) ||
    (isTemplatesTour && !isTemplatesGuidedTourVideoActive(store))
  ) {
    return '';
  }

  const periodIndex =
    videoPeriodState.periodIndex === null ||
    videoPeriodState.periodIndex === undefined
      ? ''
      : String(videoPeriodState.periodIndex);
  const videoIndex =
    videoPeriodState.videoIndex === null ||
    videoPeriodState.videoIndex === undefined
      ? ''
      : String(videoPeriodState.videoIndex);
  const videoName = getCurrentVideoName(store);

  return [periodIndex, videoIndex, videoName || 'samples-register']
    .filter(Boolean)
    .join(':');
}

function toTimestamp(value) {
  return Number.isFinite(value) && value > 0 ? value : null;
}

function normalizeTourState(rawState) {
  if (!rawState || typeof rawState !== 'object') {
    return null;
  }

  const completedAt = toTimestamp(rawState.completedAt);
  const dismissedAt = toTimestamp(rawState.dismissedAt);
  const lastShownAt = toTimestamp(rawState.lastShownAt);
  const lastAutoShownAt =
    toTimestamp(rawState.lastAutoShownAt) ||
    lastShownAt ||
    dismissedAt ||
    completedAt;
  const legacyAutoShownCount = rawState.dismissed ? 1 : 0;
  const autoShownCount = Math.max(
    0,
    Number.isFinite(rawState.autoShownCount)
      ? rawState.autoShownCount
      : legacyAutoShownCount
  );
  const inProgressAutoReshowCount = Math.max(
    0,
    Number.isFinite(rawState.inProgressAutoReshowCount)
      ? rawState.inProgressAutoReshowCount
      : 0
  );

  let status =
    typeof rawState.status === 'string' ? rawState.status : null;

  if (!status) {
    if (completedAt) {
      status = 'completed';
    } else if (rawState.dismissed || dismissedAt) {
      status = 'dismissed';
    }
  }

  return {
    ...rawState,
    completedAt,
    dismissedAt,
    lastShownAt,
    lastAutoShownAt,
    autoShownCount,
    inProgressAutoReshowCount,
    status,
  };
}

function readTourState(
  store,
  storageKeyOverride = SAMPLES_REGISTER_GUIDED_TOUR_STORAGE_KEY
) {
  try {
    const raw = localStorage.getItem(
      getStorageKey(store, storageKeyOverride)
    );
    const state = raw ? normalizeTourState(JSON.parse(raw)) : null;
    const backendTourPeriodKey = getBackendTourPeriodKey(
      store,
      storageKeyOverride
    );

    if (
      state &&
      backendTourPeriodKey &&
      state.tourPeriodKey !== backendTourPeriodKey
    ) {
      return null;
    }

    return state;
  } catch (error) {
    return null;
  }
}

function writeTourState(
  store,
  payload,
  storageKeyOverride = SAMPLES_REGISTER_GUIDED_TOUR_STORAGE_KEY
) {
  try {
    const currentState =
      readTourState(store, storageKeyOverride) || {};
    const backendTourPeriodKey = getBackendTourPeriodKey(
      store,
      storageKeyOverride
    );
    localStorage.setItem(
      getStorageKey(store, storageKeyOverride),
      JSON.stringify({
        ...currentState,
        ...payload,
        ...(backendTourPeriodKey
          ? { tourPeriodKey: backendTourPeriodKey }
          : {}),
      })
    );
  } catch (error) {
    // No-op
  }
}

function hasCompletedTour(state) {
  return !!(
    state &&
    (state.status === 'completed' || state.completedAt)
  );
}

function hasInProgressTour(state) {
  return !!(state && state.status === 'in_progress');
}

function hasReachedInProgressAutoReshowLimit(state) {
  return (
    (state?.inProgressAutoReshowCount || 0) >=
    SAMPLES_REGISTER_GUIDED_TOUR_MAX_IN_PROGRESS_AUTO_RESHOWS
  );
}

function hasReachedAutoShowLimit(state) {
  return (
    (state?.autoShownCount || 0) >=
    SAMPLES_REGISTER_GUIDED_TOUR_MAX_AUTO_SHOWS_WITHOUT_COMPLETION
  );
}

function hasReshowCooldownExpired(state) {
  if (!state?.lastAutoShownAt) {
    return true;
  }

  return (
    Date.now() - state.lastAutoShownAt >=
    SAMPLES_REGISTER_GUIDED_TOUR_RESHOW_INTERVAL_DAYS * DAY_IN_MS
  );
}

function sleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function nextFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => resolve());
  });
}

async function settleUi(vm, delay = 0) {
  if (vm && typeof vm.$nextTick === 'function') {
    await new Promise((resolve) => vm.$nextTick(resolve));
  }

  await nextFrame();
  await nextFrame();

  if (delay > 0) {
    await sleep(delay);
  }
}

function getVisibleElement(selector) {
  let elements = [];

  try {
    elements = Array.from(document.querySelectorAll(selector));
  } catch (error) {
    return null;
  }

  return (
    elements.find((element) => {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);

      return (
        rect.width > 0 &&
        rect.height > 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0'
      );
    }) || null
  );
}

function getTourTranslation(vm, key) {
  const value = vm?.$t(`samplesRegister.guidedTour.${key}`);
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeTourAssetPath(assetPath) {
  return assetPath
    .replace(/^@\/static\//, '')
    .replace(/^\/?src\/static\//, '')
    .replace(/^\/?static\//, '')
    .replace(/^\.?\//, '');
}

function getBundledTourAsset(assetPath) {
  const normalizedAssetPath = normalizeTourAssetPath(assetPath);
  const [assetGroup, ...assetFileParts] =
    normalizedAssetPath.split('/');
  const assetFile = assetFileParts.join('/');
  const assetContext = TOUR_ASSET_CONTEXTS[assetGroup];

  if (!assetContext || !assetFile) {
    return '';
  }

  try {
    const assetModule = assetContext(`./${assetFile}`);
    return assetModule?.default || assetModule || '';
  } catch (error) {
    return '';
  }
}

function resolveTourImageSrc(assetPath) {
  const normalizedAssetPath =
    typeof assetPath === 'string' ? assetPath.trim() : '';

  if (!TOUR_IMAGE_FILE_PATTERN.test(normalizedAssetPath)) {
    return '';
  }

  if (/^(https?:|data:|blob:)/i.test(normalizedAssetPath)) {
    return normalizedAssetPath;
  }

  const bundledAsset = getBundledTourAsset(normalizedAssetPath);
  if (bundledAsset) {
    return bundledAsset;
  }

  const isLocalTourAsset =
    /^(@\/static\/|\/?src\/static\/|\/?static\/|\.?\/?(img|icons)\/)/.test(
      normalizedAssetPath
    );
  const hasUnsupportedProtocol = /^[a-z][a-z\d+.-]*:/i.test(
    normalizedAssetPath
  );

  return isLocalTourAsset || hasUnsupportedProtocol
    ? ''
    : normalizedAssetPath;
}

function getTourStepImageSrc(vm, stepKey) {
  return (
    resolveTourImageSrc(
      getTourTranslation(vm, `${stepKey}.imageSrc`)
    ) || resolveTourImageSrc(TOUR_STEP_IMAGE_FALLBACKS[stepKey])
  );
}

async function waitForVisibleElement(selector) {
  const startedAt = Date.now();

  const poll = async () => {
    const element = getVisibleElement(selector);
    if (element) {
      return element;
    }

    if (Date.now() - startedAt >= TOUR_WAIT_TIMEOUT) {
      throw new Error(`driver_selector_timeout:${selector}`);
    }

    await sleep(TOUR_WAIT_INTERVAL);
    return poll();
  };

  return poll();
}

function getSamplesRegisterAnalyticsContext(vm) {
  const store = vm?.$store;
  const authenticatedUser =
    store?.state?.user?.authenticatedUser || {};
  const contact = store?.state?.contact || {};
  const contactInfo = store?.getters?.['contact/contactInfo'] || {};
  const language =
    authenticatedUser?.language_code ||
    authenticatedUser?.contacts?.[0]?.language;
  const companyCode =
    contact?.company ||
    authenticatedUser?.contacts?.[0]?.companies?.[0]?.code;
  const countryCode =
    contactInfo?.country ||
    (typeof companyCode === 'string' && companyCode.trim().length >= 2
      ? companyCode.trim().substring(0, 2)
      : undefined);

  return {
    company_code: companyCode || undefined,
    country_code: countryCode || undefined,
    user_language: language || undefined,
  };
}

function track(vm, eventName, extraParams = {}) {
  if (vm && typeof vm.trackSamplesRegisterEvent === 'function') {
    vm.trackSamplesRegisterEvent(eventName, extraParams);
    return;
  }

  if (vm && typeof vm.emitGa4Event === 'function') {
    vm.emitGa4Event(eventName, extraParams);
    return;
  }

  if (IS_LOCAL_TEST) return;

  window.gtag?.('event', eventName, {
    ...getSamplesRegisterAnalyticsContext(vm),
    ...extraParams,
  });
}

function markPendingSamplesRegisterTourStart() {
  try {
    sessionStorage.setItem(
      SAMPLES_REGISTER_TOUR_PENDING_START_KEY,
      String(Date.now())
    );
  } catch (error) {
    // No-op
  }
}

function hasPendingSamplesRegisterTourStart() {
  const pendingKeys = [
    SAMPLES_REGISTER_TOUR_PENDING_START_KEY,
    SAMPLES_REGISTER_TOUR_PENDING_INVOICE_LIST_START_KEY,
    SAMPLES_REGISTER_TOUR_PENDING_INVOICE_LIST_DETAILS_START_KEY,
    SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_CREATE_START_KEY,
    SAMPLES_REGISTER_TOUR_PENDING_TEMPLATES_LIST_START_KEY,
    SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_DETAILS_START_KEY,
    SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_RESUME_START_KEY,
  ];

  try {
    return pendingKeys.some((key) => !!sessionStorage.getItem(key));
  } catch (error) {
    return false;
  }
}

export function isSamplesRegisterGuidedTourActiveOrPending() {
  return !!(
    (activeDriver && activeDriver.isActive()) ||
    hasPendingSamplesRegisterTourStart()
  );
}

export function consumePendingSamplesRegisterTourStart() {
  try {
    const pending = sessionStorage.getItem(
      SAMPLES_REGISTER_TOUR_PENDING_START_KEY
    );
    if (!pending) {
      return false;
    }

    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_START_KEY
    );
    return true;
  } catch (error) {
    return false;
  }
}

function markPendingSamplesRegisterInvoiceListTourStart() {
  try {
    sessionStorage.setItem(
      SAMPLES_REGISTER_TOUR_PENDING_INVOICE_LIST_START_KEY,
      String(Date.now())
    );
  } catch (error) {
    // No-op
  }
}

export function consumePendingSamplesRegisterInvoiceListTourStart() {
  try {
    const pending = sessionStorage.getItem(
      SAMPLES_REGISTER_TOUR_PENDING_INVOICE_LIST_START_KEY
    );
    if (!pending) {
      return false;
    }

    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_INVOICE_LIST_START_KEY
    );
    return true;
  } catch (error) {
    return false;
  }
}

function markPendingSamplesRegisterInvoiceListDetailsTourStart() {
  try {
    sessionStorage.setItem(
      SAMPLES_REGISTER_TOUR_PENDING_INVOICE_LIST_DETAILS_START_KEY,
      String(Date.now())
    );
  } catch (error) {
    // No-op
  }
}

export function consumePendingSamplesRegisterInvoiceListDetailsTourStart() {
  try {
    const pending = sessionStorage.getItem(
      SAMPLES_REGISTER_TOUR_PENDING_INVOICE_LIST_DETAILS_START_KEY
    );
    if (!pending) {
      return false;
    }

    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_INVOICE_LIST_DETAILS_START_KEY
    );
    return true;
  } catch (error) {
    return false;
  }
}

function markPendingSamplesRegisterTemplateCreateTourStart() {
  try {
    sessionStorage.setItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_CREATE_START_KEY,
      String(Date.now())
    );
  } catch (error) {
    // No-op
  }
}

export function consumePendingSamplesRegisterTemplateCreateTourStart() {
  try {
    const pending = sessionStorage.getItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_CREATE_START_KEY
    );
    if (!pending) {
      return false;
    }

    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_CREATE_START_KEY
    );
    return true;
  } catch (error) {
    return false;
  }
}

function markPendingSamplesRegisterTemplatesListTourStart() {
  try {
    sessionStorage.setItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATES_LIST_START_KEY,
      String(Date.now())
    );
  } catch (error) {
    // No-op
  }
}

export function consumePendingSamplesRegisterTemplatesListTourStart() {
  try {
    const pending = sessionStorage.getItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATES_LIST_START_KEY
    );
    if (!pending) {
      return false;
    }

    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATES_LIST_START_KEY
    );
    return true;
  } catch (error) {
    return false;
  }
}

function markPendingSamplesRegisterTemplateDetailsTourStart() {
  try {
    sessionStorage.setItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_DETAILS_START_KEY,
      String(Date.now())
    );
  } catch (error) {
    // No-op
  }
}

export function consumePendingSamplesRegisterTemplateDetailsTourStart() {
  try {
    const pending = sessionStorage.getItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_DETAILS_START_KEY
    );
    if (!pending) {
      return false;
    }

    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_DETAILS_START_KEY
    );
    return true;
  } catch (error) {
    return false;
  }
}

function markPendingSamplesRegisterTemplateResumeTourStart() {
  try {
    sessionStorage.setItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_RESUME_START_KEY,
      String(Date.now())
    );
  } catch (error) {
    // No-op
  }
}

export function consumePendingSamplesRegisterTemplateResumeTourStart() {
  try {
    const pending = sessionStorage.getItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_RESUME_START_KEY
    );
    if (!pending) {
      return false;
    }

    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_RESUME_START_KEY
    );
    return true;
  } catch (error) {
    return false;
  }
}

function createStep({
  element,
  title,
  description,
  side = 'bottom',
  align = 'start',
  imageSrc = '',
  imageAlt = '',
  smallImagePopover = false,
  compactImagePopover = false,
  analyticsStepKey = '',
  onNext,
  onAutoAdvance,
  autoAdvanceMs = 0,
  disablePrevious = false,
  isDashboardIntro = false,
}) {
  return {
    element,
    onNext,
    onAutoAdvance,
    autoAdvanceMs,
    disablePrevious,
    isDashboardIntro,
    imageSrc,
    imageAlt,
    smallImagePopover,
    compactImagePopover,
    analyticsStepKey,
    popover: {
      title,
      description,
      side,
      align,
      disableButtons: disablePrevious ? ['previous'] : [],
    },
  };
}

function getEntryStepElement() {
  return (
    getVisibleElement(DASHBOARD_REGISTER_BUTTON_SELECTOR) ||
    getVisibleElement(
      '[data-tour-id="samples-register-top-menu-option"]'
    ) ||
    '[data-tour-id="samples-register-page-root"]'
  );
}

function getInvoiceListDetailButtonStepElement() {
  return (
    getVisibleElement('.samples-list-detail-button') ||
    '[data-tour-id="samples-list-grid"]'
  );
}

function getInvoiceDetailsSummaryStepElement() {
  return (
    getVisibleElement(
      '[data-tour-id="samples-list-details-summary-card"]'
    ) || '[data-tour-id="samples-list-details-container"]'
  );
}

function getInvoiceDetailsSummaryEditStepElement() {
  return getVisibleElement(
    '[data-tour-id="samples-list-details-summary-edit-button"]'
  );
}

function getInvoiceDetailsSampleEditStepElement() {
  return getVisibleElement(
    '.samples-list-details-edit-sample-button'
  );
}

function hasTemplateManagementAccess(vm) {
  return Boolean(vm?.$store?.getters?.['user/isAdmin']);
}

function removeTemplateSaveButtonTourTarget() {
  document
    .getElementById(TEMPLATE_SAVE_BUTTON_TOUR_TARGET_ID)
    ?.remove();
}

function getTemplateSaveButtonStepElement() {
  const button = getVisibleElement(
    EXPRESS_REGISTER_TEMPLATE_SAVE_BUTTON_SELECTOR
  );

  if (!button) {
    removeTemplateSaveButtonTourTarget();
    return (
      getVisibleElement(EXPRESS_REGISTER_GRID_SELECTOR) ||
      EXPRESS_REGISTER_GRID_SELECTOR
    );
  }

  const rect = button.getBoundingClientRect();
  const width = Math.max(1, rect.width);
  const height = Math.max(1, rect.height);

  let target = document.getElementById(
    TEMPLATE_SAVE_BUTTON_TOUR_TARGET_ID
  );

  if (!target) {
    target = document.createElement('div');
    target.id = TEMPLATE_SAVE_BUTTON_TOUR_TARGET_ID;
    target.setAttribute('aria-hidden', 'true');
    target.style.pointerEvents = 'none';
    target.style.background = 'transparent';
    target.style.border = '0';
    target.style.zIndex = '1';
    document.body.appendChild(target);
  }

  target.style.position = 'fixed';
  target.style.left = `${rect.left}px`;
  target.style.top = `${rect.top}px`;
  target.style.width = `${width}px`;
  target.style.height = `${height}px`;

  return target;
}

function getTemplatesListStepElement() {
  return (
    getVisibleElement(TEMPLATES_LIST_GRID_SELECTOR) ||
    TEMPLATES_LIST_PAGE_ROOT_SELECTOR
  );
}

function getTemplateCreateSamplesStepElement() {
  return (
    getVisibleElement(TEMPLATE_SAMPLES_GRID_SELECTOR) ||
    TEMPLATE_CREATE_PAGE_ROOT_SELECTOR
  );
}

function getTemplateDetailsPageStepElement() {
  return (
    getVisibleElement(TEMPLATE_DETAILS_PAGE_ROOT_SELECTOR) ||
    TEMPLATE_DETAILS_PAGE_ROOT_SELECTOR
  );
}

function getTemplateDetailsSummaryStepElement() {
  return (
    getVisibleElement(TEMPLATE_DETAILS_SUMMARY_SELECTOR) ||
    getTemplateDetailsPageStepElement()
  );
}

function getTemplateDetailsActionsStepElement() {
  return (
    getVisibleElement(TEMPLATE_DETAILS_ACTIONS_SELECTOR) ||
    getVisibleElement(TEMPLATE_DETAILS_LOAD_BUTTON_SELECTOR) ||
    getVisibleElement(TEMPLATE_DETAILS_EDIT_BUTTON_SELECTOR) ||
    getTemplateDetailsSummaryStepElement()
  );
}

function getTemplateDetailsLoadButtonStepElement() {
  return (
    getVisibleElement(TEMPLATE_DETAILS_LOAD_BUTTON_SELECTOR) ||
    getVisibleElement(TEMPLATE_DETAILS_ACTIONS_SELECTOR) ||
    getTemplateDetailsSummaryStepElement()
  );
}

function getTemplateDetailsEditButtonStepElement() {
  return (
    getVisibleElement(TEMPLATE_DETAILS_EDIT_BUTTON_SELECTOR) ||
    getTemplateDetailsActionsStepElement()
  );
}

function getTemplateSamplesEditButtonStepElement() {
  const editButton = Array.from(
    document.querySelectorAll(TEMPLATE_SAMPLES_EDIT_BUTTON_SELECTOR)
  ).find((element) => {
    const isInGridRows =
      element.closest('.dx-datagrid-rowsview') ||
      element.closest('.dx-datagrid-content-fixed');
    if (!isInGridRows) return false;

    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      style.opacity !== '0'
    );
  });

  return editButton || TEMPLATE_SAMPLES_EDIT_BUTTON_SELECTOR;
}

function removeInvoiceListStatusFiltersTourTarget() {
  document
    .getElementById(INVOICE_LIST_STATUS_FILTERS_TOUR_TARGET_ID)
    ?.remove();
}

function getInvoiceListStatusFiltersStepElement() {
  const filterElements = INVOICE_LIST_STATUS_FILTER_SELECTORS.map(
    (selector) => getVisibleElement(selector)
  ).filter(Boolean);

  if (!filterElements.length) {
    removeInvoiceListStatusFiltersTourTarget();
    removeTemplateSaveButtonTourTarget();
    return null;
  }

  const rects = filterElements.map((element) =>
    element.getBoundingClientRect()
  );
  const minLeft = Math.min(...rects.map((rect) => rect.left));
  const minTop = Math.min(...rects.map((rect) => rect.top));
  const maxRight = Math.max(...rects.map((rect) => rect.right));
  const maxBottom = Math.max(...rects.map((rect) => rect.bottom));
  const width = Math.max(1, maxRight - minLeft);
  const height = Math.max(1, maxBottom - minTop);

  let target = document.getElementById(
    INVOICE_LIST_STATUS_FILTERS_TOUR_TARGET_ID
  );

  if (!target) {
    target = document.createElement('div');
    target.id = INVOICE_LIST_STATUS_FILTERS_TOUR_TARGET_ID;
    target.setAttribute('aria-hidden', 'true');
    target.style.pointerEvents = 'none';
    target.style.background = 'transparent';
    target.style.border = '0';
    target.style.zIndex = '1';
    document.body.appendChild(target);
  }

  target.style.position = 'fixed';
  target.style.left = `${minLeft}px`;
  target.style.top = `${minTop}px`;
  target.style.width = `${width}px`;
  target.style.height = `${height}px`;

  return target;
}

function renderSkipButton(popover, label, onSkip) {
  const footerButtons = popover?.footerButtons;
  if (!footerButtons) return;

  const existingButton = footerButtons.querySelector(
    `.${TOUR_SKIP_BUTTON_CLASS}`
  );
  if (existingButton) {
    existingButton.textContent = label;
    return;
  }

  const skipButton = document.createElement('button');
  skipButton.type = 'button';
  skipButton.className = TOUR_SKIP_BUTTON_CLASS;
  skipButton.textContent = label;
  skipButton.addEventListener('click', onSkip);
  footerButtons.prepend(skipButton);
}

function ensureTourPopoverStyles() {
  if (document.getElementById(TOUR_POPOVER_STYLES_ID)) {
    return;
  }

  const styleTag = document.createElement('style');
  styleTag.id = TOUR_POPOVER_STYLES_ID;
  styleTag.textContent = `
    .${TOUR_POPOVER_CLASS} {
      max-width: 520px;
      padding: 18px 18px 14px;
      border: 1px solid #cfe0f4;
      border-radius: 18px;
      box-shadow: 0 10px 42px rgba(0, 52, 106, 0.3);
      background:
        radial-gradient(circle at top right, rgba(4, 80, 159, 0.14), transparent 34%),
        linear-gradient(180deg, #ffffff 0%, #f5f9ff 100%);
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-arrow {
      border-color: #ffffff;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-close-btn {
      color: #7f9dbd;
      top: 10px;
      right: 10px;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-close-btn:hover,
    .${TOUR_POPOVER_CLASS} .driver-popover-close-btn:focus {
      color: #00346a;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-title {
      margin: 0;
      color: #00346a;
      font-size: 16px;
      font-weight: 700;
      line-height: 1.35;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-description {
      margin-top: 8px;
      color: #35506d;
      font-size: 13px;
      line-height: 1.55;
      white-space: pre-line;
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_POPOVER_WITH_IMAGE_CLASS} {
      width: min(1260px, calc(100vw - 56px));
      max-width: min(1260px, calc(100vw - 56px));
      transform: translateY(-40px);
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_POPOVER_WITH_IMAGE_CLASS} .driver-popover-description {
      max-width: none;
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_SMALL_IMAGE_POPOVER_CLASS} {
      width: min(900px, calc(100vw - 56px));
      max-width: min(900px, calc(100vw - 56px));
      transform: translateY(-16px);
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_SMALL_IMAGE_POPOVER_CLASS} .samples-register-tour-popover-step-image-wrap {
      display: flex;
      justify-content: center;
      padding: 8px;
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_SMALL_IMAGE_POPOVER_CLASS} .samples-register-tour-popover-step-image {
      width: auto;
      max-width: 100%;
      max-height: min(36vh, 300px);
      object-fit: contain;
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_INVOICE_DETAILS_COMPACT_POPOVER_CLASS} {
      width: min(1080px, calc(100vw - 56px));
      max-width: min(1080px, calc(100vw - 56px));
      transform: translateY(-30px);
    }

    .samples-register-tour-popover-step-image-wrap {
      margin-top: 12px;
      border-radius: 12px;
      overflow: hidden;
      border: 1px solid rgba(0, 52, 106, 0.12);
      background: #ffffff;
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_INVOICE_DETAILS_COMPACT_POPOVER_CLASS} .samples-register-tour-popover-step-image-wrap {
      display: flex;
      justify-content: center;
      padding: 8px;
    }

    .samples-register-tour-popover-step-image {
      display: block;
      width: 100%;
      max-height: min(62vh, 560px);
      object-fit: contain;
      background: #ffffff;
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_INVOICE_DETAILS_COMPACT_POPOVER_CLASS} .samples-register-tour-popover-step-image {
      width: auto;
      max-width: 100%;
      max-height: min(52vh, 440px);
      object-fit: contain;
    }

    @media (min-width: 769px) and (max-height: 900px) {
      .${TOUR_POPOVER_CLASS}.${TOUR_POPOVER_WITH_IMAGE_CLASS} {
        width: min(1120px, calc(100vw - 40px));
        max-width: min(1120px, calc(100vw - 40px));
        transform: translateY(-56px);
      }

      .${TOUR_POPOVER_CLASS}.${TOUR_SMALL_IMAGE_POPOVER_CLASS} {
        width: min(840px, calc(100vw - 40px));
        max-width: min(840px, calc(100vw - 40px));
      }

      .${TOUR_POPOVER_CLASS}.${TOUR_INVOICE_DETAILS_COMPACT_POPOVER_CLASS} {
        width: min(980px, calc(100vw - 40px));
        max-width: min(980px, calc(100vw - 40px));
        transform: translateY(-22px);
      }

      .samples-register-tour-popover-step-image {
        max-height: min(48vh, 400px);
      }

      .${TOUR_POPOVER_CLASS}.${TOUR_INVOICE_DETAILS_COMPACT_POPOVER_CLASS} .samples-register-tour-popover-step-image {
        max-height: min(44vh, 360px);
      }

      .${TOUR_POPOVER_CLASS}.${TOUR_SMALL_IMAGE_POPOVER_CLASS} .samples-register-tour-popover-step-image {
        max-height: min(32vh, 250px);
      }
    }

    @media (max-width: 768px) {
      .${TOUR_POPOVER_CLASS}.${TOUR_POPOVER_WITH_IMAGE_CLASS} {
        width: calc(100vw - 20px);
        max-width: calc(100vw - 20px);
        transform: translateY(-22px);
      }

      .samples-register-tour-popover-step-image {
        max-height: min(46vh, 420px);
      }

      .${TOUR_POPOVER_CLASS}.${TOUR_SMALL_IMAGE_POPOVER_CLASS} .samples-register-tour-popover-step-image {
        max-height: min(28vh, 220px);
      }

      .${TOUR_POPOVER_CLASS}.${TOUR_INVOICE_DETAILS_COMPACT_POPOVER_CLASS} .samples-register-tour-popover-step-image {
        max-height: min(38vh, 300px);
      }
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-footer {
      margin-top: 16px;
      padding-top: 12px;
      border-top: 1px solid rgba(0, 52, 106, 0.08);
      gap: 8px;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-navigation-btns {
      gap: 6px;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-footer button {
      padding: 7px 12px;
      border-radius: 10px;
      border: 1px solid #c9d9ea;
      background: #ffffff;
      color: #00346a;
      font-size: 12px;
      font-weight: 600;
      text-shadow: none;
      box-shadow: none;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-footer button:hover,
    .${TOUR_POPOVER_CLASS} .driver-popover-footer button:focus {
      background: #eef5fc;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-next-btn {
      border-color: #00346a;
      background: #00346a;
      color: #ffffff;
    }

    .${TOUR_POPOVER_CLASS} .driver-popover-next-btn:hover,
    .${TOUR_POPOVER_CLASS} .driver-popover-next-btn:focus {
      background: #04509f;
    }

    .${TOUR_POPOVER_CLASS} .${TOUR_SKIP_BUTTON_CLASS} {
      border-color: transparent;
      background: transparent;
      color: #6e89a6;
    }

    .${TOUR_POPOVER_CLASS} .${TOUR_SKIP_BUTTON_CLASS}:hover,
    .${TOUR_POPOVER_CLASS} .${TOUR_SKIP_BUTTON_CLASS}:focus {
      background: rgba(4, 80, 159, 0.08);
      color: #00346a;
    }

    .samples-register-tour-popover-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 20px;
      padding-right: 28px;
    }

    .samples-register-tour-popover-avatar {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: linear-gradient(180deg, #ffffff 0%, #eaf3ff 100%);
      border: 1px solid #cfe0f4;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .samples-register-tour-popover-avatar img {
      width: 28px;
      height: 28px;
      display: block;
    }

    .samples-register-tour-popover-meta {
      min-width: 0;
    }

    .samples-register-tour-popover-name {
      color: #00346a;
      font-size: 13px;
      font-weight: 700;
      line-height: 1.2;
    }

    .samples-register-tour-popover-role {
      margin-top: 2px;
      color: #6e89a6;
      font-size: 11px;
      font-weight: 500;
      line-height: 1.2;
    }

    .${TOUR_POPOVER_CLASS}.${TOUR_DASHBOARD_POPOVER_CLASS} {
      margin-top: clamp(70px, 10vh, 130px);
      margin-left: clamp(-220px, -12vw, -36px);
      max-width: min(720px, calc(100vw - 48px));
    }

    /* Keep the secondary bubble visible when driver.js highlights it.
       driver.css sets overflow:hidden on the parent of .driver-active-element. */
    .driver-active .bubble-toggle-group:has(> .driver-active-element) {
      overflow: visible !important;
    }

    @media (max-width: 1360px) {
      .${TOUR_POPOVER_CLASS}.${TOUR_DASHBOARD_POPOVER_CLASS} {
        margin-left: clamp(-260px, -16vw, -80px);
        max-width: min(660px, calc(100vw - 40px));
      }
    }
  `;

  document.head.appendChild(styleTag);
}

function renderPopoverStepImage(popover, step = null, vm = null) {
  const wrapper = popover?.wrapper;
  const description = popover?.description;
  if (!wrapper || !description) return;

  const existingWrap = wrapper.querySelector(
    '.samples-register-tour-popover-step-image-wrap'
  );
  const normalizedImageSrc =
    typeof step?.imageSrc === 'string' ? step.imageSrc.trim() : '';

  if (!normalizedImageSrc) {
    wrapper.classList.remove(TOUR_POPOVER_WITH_IMAGE_CLASS);
    existingWrap?.remove();
    return;
  }
  wrapper.classList.add(TOUR_POPOVER_WITH_IMAGE_CLASS);

  let imageWrap = existingWrap;
  if (!imageWrap) {
    imageWrap = document.createElement('div');
    imageWrap.className =
      'samples-register-tour-popover-step-image-wrap';
    description.insertAdjacentElement('afterend', imageWrap);
  }

  let image = imageWrap.querySelector('img');
  if (!image) {
    image = document.createElement('img');
    image.className = 'samples-register-tour-popover-step-image';
    imageWrap.appendChild(image);
  }

  image.src = normalizedImageSrc;
  image.alt =
    step?.imageAlt ||
    step?.title ||
    vm?.$t('samplesRegister.guidedTour.defaultImageAlt');
}

function enhancePopover(popover, { title, step, vm }) {
  const wrapper = popover?.wrapper;
  if (!wrapper) return;

  ensureTourPopoverStyles();
  wrapper.classList.add(TOUR_POPOVER_CLASS);
  wrapper.classList.toggle(
    TOUR_SMALL_IMAGE_POPOVER_CLASS,
    Boolean(step?.smallImagePopover)
  );
  wrapper.classList.toggle(
    TOUR_INVOICE_DETAILS_COMPACT_POPOVER_CLASS,
    Boolean(step?.compactImagePopover)
  );

  if (
    !wrapper.querySelector('.samples-register-tour-popover-header')
  ) {
    const assistantIconAlt = vm?.$t(
      'samplesRegister.guidedTour.popover.iconAlt'
    );
    const assistantName = vm?.$t(
      'samplesRegister.guidedTour.popover.assistantName'
    );
    const assistantRole = vm?.$t(
      'samplesRegister.guidedTour.popover.assistantRole'
    );

    const header = document.createElement('div');
    header.className = 'samples-register-tour-popover-header';

    const avatar = document.createElement('div');
    avatar.className = 'samples-register-tour-popover-avatar';

    const avatarImage = document.createElement('img');
    avatarImage.src = tourAssistantIcon;
    avatarImage.alt = assistantIconAlt;
    avatar.appendChild(avatarImage);

    const meta = document.createElement('div');
    meta.className = 'samples-register-tour-popover-meta';

    const name = document.createElement('div');
    name.className = 'samples-register-tour-popover-name';
    name.textContent = assistantName;

    const role = document.createElement('div');
    role.className = 'samples-register-tour-popover-role';
    role.textContent = assistantRole;

    meta.appendChild(name);
    meta.appendChild(role);
    header.appendChild(avatar);
    header.appendChild(meta);

    wrapper.insertBefore(header, wrapper.firstChild);
  }

  if (popover.title && title) {
    popover.title.textContent = title;
  }

  renderPopoverStepImage(popover, step, vm);
}

function buildSteps(
  vm,
  { onContinueToInvoiceList } = {}
) {
  const $t = (key) => vm.$t(`samplesRegister.guidedTour.${key}`);

  return [
    createStep({
      element: getEntryStepElement(),
      title: $t('intro.title'),
      description: $t('intro.description'),
      side: 'bottom',
      align: 'center',
    }),
    createStep({
      element: '[data-tour-id="samples-register-standard-form"]',
      title: $t('standardForm.title'),
      description: $t('standardForm.description'),
      side: 'top',
    }),
    createStep({
      element:
        '[data-tour-id="samples-register-standard-save-button"]',
      title: $t('standardSave.title'),
      description: $t('standardSave.description'),
      side: 'top',
      onNext: async (driverObj) => {
        await vm.showTourStandardSavePopup?.();
        await settleUi(vm, 200);
        await waitForVisibleElement(
          '[data-tour-id="samples-register-standard-save-popup"]'
        );
        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      element:
        '[data-tour-id="samples-register-standard-save-popup"]',
      title: $t('standardSavePopup.title'),
      description: $t('standardSavePopup.description'),
      side: 'top',
      disablePrevious: true,
      onNext: async (driverObj) => {
        await vm.hideTourStandardSavePopup?.();
        await settleUi(vm, 120);
        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      element:
        '[data-tour-id="samples-register-express-toggle-button"]',
      title: $t('expressMode.title'),
      description: $t('expressMode.description'),
      side: 'left',
      onNext: async (driverObj) => {
        await vm.enableExpressModeForTour?.();
        await settleUi(vm, 260);
        await waitForVisibleElement(
          '[data-tour-id="samples-register-express-form"]'
        );
        await waitForVisibleElement(
          '[data-tour-id="samples-register-express-fields-edit-button"]'
        );
        await settleUi(vm, 120);
        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      element:
        '[data-tour-id="samples-register-express-fields-edit-button"]',
      title: $t('visibleFieldsButton.title'),
      description: $t('visibleFieldsButton.description'),
      side: 'left',
      onNext: async (driverObj) => {
        await vm.openTourVisibleFieldsPopup?.();
        await settleUi(vm, 160);
        await waitForVisibleElement(
          '[data-tour-id="samples-register-visible-fields-popup"]'
        );
        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      element:
        '[data-tour-id="samples-register-visible-fields-popup"]',
      title: $t('visibleFieldsPopup.title'),
      description: $t('visibleFieldsPopup.description'),
      side: 'top',
      disablePrevious: true,
      onNext: async (driverObj) => {
        await vm.closeTourVisibleFieldsPopup?.();
        await settleUi(vm, 160);
        await waitForVisibleElement(
          '[data-tour-id="samples-register-express-grid"]'
        );
        await settleUi(vm, 120);
        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      element: '[data-tour-id="samples-register-express-grid"]',
      title: $t('expressGrid.title'),
      description: $t('expressGrid.description'),
      side: 'top',
      align: 'center',
      imageSrc: getTourStepImageSrc(vm, 'expressGrid'),
      imageAlt: $t('expressGrid.imageAlt'),
      onNext: async (driverObj) => {
        await vm.setTourTab?.(1);
        await settleUi(vm, 220);
        await waitForVisibleElement(
          '[data-tour-id="samples-register-confirmation-panel"]'
        );
        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      element: '[data-tour-id="samples-register-confirmation-panel"]',
      title: $t('confirmationPanel.title'),
      description: $t('confirmationPanel.description'),
      side: 'top',
      imageSrc: getTourStepImageSrc(vm, 'confirmationPanel'),
      imageAlt: $t('confirmationPanel.imageAlt'),
      onNext: async (driverObj) => {
        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      element: '[data-tour-id="samples-register-save-draft-button"]',
      title: $t('draftManagement.title'),
      description: $t('draftManagement.description'),
      side: 'top',
      onNext: async (driverObj) => {
        await vm.setTourTab?.(2);
        await settleUi(vm, 220);
        await waitForVisibleElement(
          '[data-tour-id="samples-register-shipping-panel"]'
        );
        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      element: '[data-tour-id="samples-register-shipping-panel"]',
      title: $t('shippingPanel.title'),
      description: $t('shippingPanel.description'),
      side: 'top',
      onNext: async (driverObj) => {
        await vm.showTourFinalConfirmationPopup?.();
        await settleUi(vm, 180);
        await waitForVisibleElement(
          '[data-tour-id="samples-register-final-popup"]'
        );
        if (driverObj.isActive()) {
          driverObj.moveNext();
        }
      },
    }),
    createStep({
      element: '[data-tour-id="samples-register-final-popup"]',
      title: $t('finalConfirmation.title'),
      description: $t('finalConfirmation.description'),
      side: 'top',
      disablePrevious: true,
      onNext: async (driverObj) => {
        if (typeof onContinueToInvoiceList === 'function') {
          await onContinueToInvoiceList(driverObj);
          return;
        }

        if (driverObj.isActive()) {
          driverObj.destroy();
        }
      },
    }),
  ];
}

function buildFormSteps(vm, options = {}) {
  return buildSteps(vm, options).slice(1);
}

function buildFormTemplateResumeSteps(vm, options = {}) {
  const steps = buildFormSteps(vm, options);
  const expressGridTitle = vm.$t(
    'samplesRegister.guidedTour.expressGrid.title'
  );
  const startIndex = steps.findIndex(
    (step) => step?.popover?.title === expressGridTitle
  );
  const startIndexByElement = steps.findIndex(
    (step) => step?.element === EXPRESS_REGISTER_GRID_SELECTOR
  );

  return steps.slice(
    startIndex >= 0
      ? startIndex
      : startIndexByElement >= 0
      ? startIndexByElement
      : 0
  );
}

function buildTemplateCreateSteps(
  vm,
  { onContinueToTemplatesList } = {}
) {
  const $t = (key) => vm.$t(`samplesRegister.guidedTour.${key}`);
  return [
    createStep({
      element:
        getVisibleElement(TEMPLATE_CREATE_FORM_SELECTOR) ||
        TEMPLATE_CREATE_PAGE_ROOT_SELECTOR,
      title: $t('templateCreateIntro.title'),
      description: $t('templateCreateIntro.description'),
      side: 'bottom',
      align: 'center',
      imageSrc: getTourStepImageSrc(vm, 'templateCreateIntro'),
      imageAlt: getTourTranslation(
        vm,
        'templateCreateIntro.imageAlt'
      ),
      disablePrevious: true,
    }),
    createStep({
      element: getTemplateCreateSamplesStepElement(),
      title: $t('templateCreateSamplesList.title'),
      description: $t('templateCreateSamplesList.description'),
      side: 'top',
      align: 'center',
      imageSrc: getTourStepImageSrc(vm, 'templateCreateSamplesList'),
      imageAlt: getTourTranslation(
        vm,
        'templateCreateSamplesList.imageAlt'
      ),
      onNext: async (driverObj) => {
        if (typeof onContinueToTemplatesList === 'function') {
          await onContinueToTemplatesList(driverObj);
          return;
        }

        if (driverObj.isActive()) {
          driverObj.destroy();
        }
      },
    }),
  ];
}

function buildTemplatesListSteps(
  vm,
  { onContinueToTemplateDetails, onContinueToRegister } = {}
) {
  const $t = (key) => vm.$t(`samplesRegister.guidedTour.${key}`);

  return [
    createStep({
      element: getTemplatesListStepElement(),
      title: $t('templatesListIntro.title'),
      description: $t('templatesListIntro.description'),
      side: 'bottom',
      align: 'center',
      imageSrc: getTourStepImageSrc(vm, 'templatesListIntro'),
      imageAlt: getTourTranslation(
        vm,
        'templatesListIntro.imageAlt'
      ),
      disablePrevious: true,
      onNext: async (driverObj) => {
        if (typeof onContinueToTemplateDetails === 'function') {
          const openedTemplateDetails =
            await onContinueToTemplateDetails(driverObj);
          if (openedTemplateDetails) {
            return;
          }
        }

        if (typeof onContinueToRegister === 'function') {
          await onContinueToRegister(driverObj);
          return;
        }

        if (driverObj.isActive()) {
          driverObj.destroy();
        }
      },
    }),
  ];
}

function buildTemplateDetailsSteps(
  vm,
  { onContinueToRegister } = {}
) {
  const $t = (key) => vm.$t(`samplesRegister.guidedTour.${key}`);
  const showAdminTemplateEditionSteps =
    hasTemplateManagementAccess(vm);
  const steps = [
    createStep({
      element: getTemplateDetailsPageStepElement(),
      title: $t('templateDetailsIntro.title'),
      description: $t('templateDetailsIntro.description'),
      side: 'left',
      align: 'center',
      imageSrc: getTourStepImageSrc(vm, 'templateDetailsIntro'),
      imageAlt: getTourTranslation(
        vm,
        'templateDetailsIntro.imageAlt'
      ),
      disablePrevious: true,
    }),
    createStep({
      element: getTemplateDetailsSummaryStepElement(),
      title: $t('templateDetailsSummary.title'),
      description: $t('templateDetailsSummary.description'),
      side: 'left',
      align: 'center',
      imageSrc: getTourStepImageSrc(vm, 'templateDetailsSummary'),
      imageAlt: getTourTranslation(
        vm,
        'templateDetailsSummary.imageAlt'
      ),
    }),
    createStep({
      element: getTemplateDetailsLoadButtonStepElement(),
      title: $t('templateDetailsActions.title'),
      description: $t('templateDetailsActions.description'),
      side: 'left',
      align: 'center',
      imageSrc: getTourStepImageSrc(vm, 'templateDetailsActions'),
      imageAlt: getTourTranslation(
        vm,
        'templateDetailsActions.imageAlt'
      ),
    }),
  ];

  if (showAdminTemplateEditionSteps) {
    const templateDetailsSamplesEditStep = createStep({
      element: getTemplateSamplesEditButtonStepElement(),
      title: $t('templateDetailsSamplesEdit.title'),
      description: $t('templateDetailsSamplesEdit.description'),
      side: 'left',
      align: 'center',
      imageSrc: getTourStepImageSrc(
        vm,
        'templateDetailsSamplesEdit'
      ),
      imageAlt: getTourTranslation(
        vm,
        'templateDetailsSamplesEdit.imageAlt'
      ),
      smallImagePopover: true,
    });

    steps.push(
      createStep({
        element: getTemplateDetailsEditButtonStepElement(),
        title: $t('templateDetailsEditableMeta.title'),
        description: $t('templateDetailsEditableMeta.description'),
        side: 'left',
        align: 'center',
        imageSrc: getTourStepImageSrc(
          vm,
          'templateDetailsEditableMeta'
        ),
        imageAlt: getTourTranslation(
          vm,
          'templateDetailsEditableMeta.imageAlt'
        ),
        onNext: async (driverObj) => {
          try {
            const editButton = await waitForVisibleElement(
              TEMPLATE_SAMPLES_EDIT_BUTTON_SELECTOR
            );
            templateDetailsSamplesEditStep.element = editButton;
          } catch (error) {
            templateDetailsSamplesEditStep.element =
              getVisibleElement(TEMPLATE_SAMPLES_GRID_SELECTOR) ||
              TEMPLATE_SAMPLES_GRID_SELECTOR;
          }

          if (driverObj.hasNextStep()) {
            driverObj.moveNext();
          }
        },
      })
    );
    steps.push(templateDetailsSamplesEditStep);
  }

  const finalStep = steps[steps.length - 1];
  finalStep.onNext = async (driverObj) => {
    if (typeof onContinueToRegister === 'function') {
      await onContinueToRegister(driverObj);
      return;
    }

    if (driverObj.isActive()) {
      driverObj.destroy();
    }
  };

  return steps;
}

function buildDashboardIntroSteps(vm, { onContinueToForm } = {}) {
  const $t = (key) => vm.$t(`samplesRegister.guidedTour.${key}`);
  const introTitle = $t('intro.title');
  const introDescription = $t('intro.description');

  return [
    createStep({
      element:
        getVisibleElement(DASHBOARD_REGISTER_BUTTON_SELECTOR) ||
        getVisibleElement(
          '[data-tour-id="samples-register-top-menu-option"]'
        ) ||
        DASHBOARD_REGISTER_BUTTON_SELECTOR,
      title: introTitle,
      description: introDescription,
      side: 'bottom',
      align: 'center',
      isDashboardIntro: true,
      disablePrevious: true,
      onNext: async (driverObj) => {
        if (typeof onContinueToForm === 'function') {
          await onContinueToForm(driverObj);
          return;
        }

        markPendingSamplesRegisterTourStart();
        const registerButton = getVisibleElement(
          DASHBOARD_REGISTER_BUTTON_SELECTOR
        );
        registerButton?.click();
        if (driverObj.isActive()) {
          driverObj.destroy();
        }
      },
    }),
  ];
}

function buildTemplatesDashboardIntroSteps(
  vm,
  { onContinueToTemplatesList } = {}
) {
  const $t = (key) => vm.$t(`samplesRegister.guidedTour.${key}`);

  return [
    createStep({
      element:
        getVisibleElement(DASHBOARD_TEMPLATES_BUTTON_SELECTOR) ||
        DASHBOARD_TEMPLATES_BUTTON_SELECTOR,
      title: $t('templateFlowIntro.title'),
      description: $t('templateFlowIntro.description'),
      side: 'bottom',
      align: 'center',
      isDashboardIntro: true,
      disablePrevious: true,
      imageSrc: getTourStepImageSrc(vm, 'templateFlowIntro'),
      imageAlt: getTourTranslation(
        vm,
        'templateFlowIntro.imageAlt'
      ),
      onNext: async (driverObj) => {
        if (typeof onContinueToTemplatesList === 'function') {
          await onContinueToTemplatesList(driverObj);
          return;
        }

        markPendingSamplesRegisterTemplatesListTourStart();
        const templatesButton = getVisibleElement(
          DASHBOARD_TEMPLATES_BUTTON_SELECTOR
        );
        templatesButton?.click();
        if (driverObj.isActive()) {
          driverObj.destroy();
        }
      },
    }),
  ];
}

function buildInvoiceListSteps(vm, { onContinueToDetails } = {}) {
  const $t = (key) => vm.$t(`samplesRegister.guidedTour.${key}`);

  return [
    createStep({
      element: '[data-tour-id="samples-list-page-root"]',
      title: $t('invoiceListIntro.title'),
      description: $t('invoiceListIntro.description'),
      side: 'bottom',
      align: 'center',
      imageSrc: getTourStepImageSrc(vm, 'invoiceListIntro'),
      imageAlt: $t('invoiceListIntro.imageAlt'),
      disablePrevious: true,
    }),
    createStep({
      element: () => getInvoiceListStatusFiltersStepElement(),
      title: $t('statusFilters.title'),
      description: $t('statusFilters.description'),
      side: 'bottom',
    }),
    createStep({
      element: '[data-tour-id="samples-list-grid"]',
      title: $t('invoiceManagement.title'),
      description: $t('invoiceManagement.description'),
      side: 'top',
      imageSrc: getTourStepImageSrc(vm, 'invoiceManagement'),
      imageAlt: $t('invoiceManagement.imageAlt'),
    }),
    createStep({
      element: getInvoiceListDetailButtonStepElement(),
      title: $t('invoiceDetailFromList.title'),
      description: $t('invoiceDetailFromList.description'),
      side: 'left',
      imageSrc: getTourStepImageSrc(vm, 'invoiceDetailFromList'),
      imageAlt: $t('invoiceDetailFromList.imageAlt'),
      smallImagePopover: true,
      onNext: async (driverObj) => {
        if (typeof onContinueToDetails === 'function') {
          await onContinueToDetails(driverObj);
          return;
        }

        if (driverObj.isActive()) {
          driverObj.destroy();
        }
      },
    }),
  ];
}

function buildInvoiceListDetailsSteps(vm, { onFinish } = {}) {
  const $t = (key) => vm.$t(`samplesRegister.guidedTour.${key}`);
  const steps = [
    createStep({
      element: getInvoiceDetailsSummaryStepElement(),
      title: $t('invoiceDetailsSummary.title'),
      description: $t('invoiceDetailsSummary.description'),
      side: 'bottom',
      align: 'center',
      analyticsStepKey: 'invoice_details_summary',
      disablePrevious: true,
    }),
    createStep({
      element: () =>
        getInvoiceDetailsSummaryEditStepElement() ||
        getInvoiceDetailsSummaryStepElement(),
      title: $t('invoiceDetailsHeaderEdit.title'),
      description: $t('invoiceDetailsHeaderEdit.description'),
      side: 'left',
      analyticsStepKey: 'invoice_details_header_edit',
    }),
    createStep({
      element: '[data-tour-id="samples-list-details-grid"]',
      title: $t('invoiceDetails.title'),
      description: $t('invoiceDetails.description'),
      side: 'top',
      align: 'center',
      imageSrc: getTourStepImageSrc(vm, 'invoiceDetails'),
      imageAlt: $t('invoiceDetails.imageAlt'),
      compactImagePopover: true,
      analyticsStepKey: 'invoice_details_samples_grid',
    }),
    createStep({
      element: () =>
        getInvoiceDetailsSampleEditStepElement() ||
        '[data-tour-id="samples-list-details-grid"]',
      title: $t('invoiceDetailsSampleEdit.title'),
      description: $t('invoiceDetailsSampleEdit.description'),
      side: 'left',
      smallImagePopover: true,
      analyticsStepKey: 'invoice_details_sample_edit',
    }),
  ];

  const finalStep = steps[steps.length - 1];
  finalStep.onNext = async (driverObj) => {
    if (typeof onFinish === 'function') {
      await onFinish(driverObj);
      return;
    }

    if (driverObj.isActive()) {
      driverObj.destroy();
    }
  };

  return steps;
}

export function shouldAutoStartSamplesRegisterGuidedTour(
  store,
  {
    force = false,
    storageKeyOverride = SAMPLES_REGISTER_GUIDED_TOUR_STORAGE_KEY,
  } = {}
) {
  if (!ENABLE_SAMPLES_REGISTER_GUIDED_TOUR) return false;
  if (force) return true;

  const state = readTourState(store, storageKeyOverride);
  if (!state) return true;
  if (hasInProgressTour(state)) {
    if (!hasReachedInProgressAutoReshowLimit(state)) {
      return true;
    }

    writeTourState(
      store,
      {
        dismissed: true,
        status: 'dismissed',
        dismissedAt: Date.now(),
      },
      storageKeyOverride
    );
    return false;
  }
  if (!hasCompletedTour(state) && hasReachedAutoShowLimit(state)) {
    return false;
  }

  return hasReshowCooldownExpired(state);
}

export function shouldAutoStartSamplesRegisterTemplatesGuidedTour(
  store,
  { force = false } = {}
) {
  if (!force && !isTemplatesGuidedTourVideoActive(store)) {
    return false;
  }

  return shouldAutoStartSamplesRegisterGuidedTour(store, {
    force,
    storageKeyOverride:
      SAMPLES_REGISTER_TEMPLATES_GUIDED_TOUR_STORAGE_KEY,
  });
}

export function resetSamplesRegisterGuidedTour(store) {
  try {
    localStorage.removeItem(getStorageKey(store));
    localStorage.removeItem(
      getStorageKey(
        store,
        SAMPLES_REGISTER_TEMPLATES_GUIDED_TOUR_STORAGE_KEY
      )
    );
    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_START_KEY
    );
    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_INVOICE_LIST_START_KEY
    );
    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_INVOICE_LIST_DETAILS_START_KEY
    );
    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_CREATE_START_KEY
    );
    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATES_LIST_START_KEY
    );
    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_DETAILS_START_KEY
    );
    sessionStorage.removeItem(
      SAMPLES_REGISTER_TOUR_PENDING_TEMPLATE_RESUME_START_KEY
    );
  } catch (error) {
    // No-op
  }
}

export function stopSamplesRegisterGuidedTour(ownerVm = null) {
  if (
    ownerVm &&
    activeDriverOwnerVm &&
    activeDriverOwnerVm !== ownerVm
  ) {
    return;
  }

  clearAutoAdvanceTimeout();
  removeInvoiceListStatusFiltersTourTarget();
  removeTemplateSaveButtonTourTarget();

  if (activeDriver && activeDriver.isActive()) {
    activeDriver.destroy();
    return;
  }

  activeDriver = null;
  activeDriverOwnerVm = null;
  unlockTourScroll();
}

export function startSamplesRegisterTemplatesDashboardIntroTour(
  vm,
  { force = false } = {}
) {
  if (
    !vm ||
    !vm.$store ||
    !shouldAutoStartSamplesRegisterTemplatesGuidedTour(
      vm.$store,
      { force }
    )
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    return activeDriver;
  }

  let completionStatus = null;
  let completionAction = null;
  let hasTrackedEngagement = false;
  const storageKeyOverride =
    SAMPLES_REGISTER_TEMPLATES_GUIDED_TOUR_STORAGE_KEY;
  const tourFlow = 'templates_dashboard_intro';
  const steps = buildTemplatesDashboardIntroSteps(vm, {
    onContinueToTemplatesList: async (driverObj) => {
      markPendingSamplesRegisterTemplatesListTourStart();
      completionStatus = 'continue_to_templates_list';
      completionAction = 'continue_button';

      const templatesButton = getVisibleElement(
        DASHBOARD_TEMPLATES_BUTTON_SELECTOR
      );
      if (templatesButton) {
        templatesButton.click();
      } else {
        vm.$f7router?.navigate(
          {
            name: 'TemplatesListPage',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      }

      if (driverObj.isActive()) {
        driverObj.destroy();
      }
    },
  });
  if (!steps.length) return null;

  const currentState = readTourState(
    vm.$store,
    storageKeyOverride
  );
  const wasPreviouslyCompleted = hasCompletedTour(currentState);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const isInProgressAutoStart =
    isAutoStart && hasInProgressTour(currentState);
  const autoShownCount = isAutoStart
    ? (currentState?.autoShownCount || 0) + 1
    : currentState?.autoShownCount || 0;
  const inProgressAutoReshowCount = isInProgressAutoStart
    ? (currentState?.inProgressAutoReshowCount || 0) + 1
    : currentState?.inProgressAutoReshowCount || 0;

  writeTourState(
    vm.$store,
    {
      dismissed: false,
      status: wasPreviouslyCompleted ? 'completed' : 'in_progress',
      lastShownAt: startedAt,
      ...(isAutoStart
        ? {
            autoShownCount,
            inProgressAutoReshowCount,
            lastAutoShownAt: startedAt,
          }
        : {
            lastManualShownAt: startedAt,
          }),
    },
    storageKeyOverride
  );

  const nextBtnText = vm.$t('next');
  const prevBtnText = vm.$t('agroIA.tour.previous');
  const doneBtnText = nextBtnText;
  const skipBtnText = vm.$t('agroIA.tour.skip');

  const driverObj = driver({
    overlayClickBehavior: () => {
      return false;
    },
    stagePadding: 4,
    stageRadius: 10,
    showProgress: false,
    nextBtnText,
    prevBtnText,
    doneBtnText,
    onPopoverRender: (popover) => {
      const step = driverObj.getActiveStep();
      const isDashboardIntro = Boolean(step?.isDashboardIntro);
      enhancePopover(popover, {
        title: popover?.title?.textContent || '',
        step,
        vm,
      });
      if (popover?.wrapper) {
        popover.wrapper.classList.toggle(
          TOUR_DASHBOARD_POPOVER_CLASS,
          isDashboardIntro
        );
      }
      renderSkipButton(popover, skipBtnText, () => {
        completionStatus = 'dismissed';
        completionAction = 'skip_button';
        driverObj.destroy();
      });
    },
    onNextClick: async () => {
      const activeIndex = driverObj.getActiveIndex();
      if (!hasTrackedEngagement && activeIndex === 0) {
        hasTrackedEngagement = true;
        track(vm, 'samples_register_guided_tour_engaged', {
          tour_flow: tourFlow,
          auto_show_count: autoShownCount,
        });
      }

      const step = driverObj.getActiveStep();

      if (step && typeof step.onNext === 'function') {
        try {
          await step.onNext(driverObj);
        } catch (error) {
          // No-op
        }
        return;
      }

      if (driverObj.hasNextStep()) {
        driverObj.moveNext();
        return;
      }

      completionStatus = 'completed';
      completionAction = 'finish_button';
      driverObj.destroy();
    },
    onPrevClick: () => {
      const step = driverObj.getActiveStep();
      if (step && step.disablePrevious) {
        return;
      }

      if (driverObj.hasPreviousStep()) {
        driverObj.movePrevious();
      }
    },
    onCloseClick: () => {
      completionStatus = 'dismissed';
      completionAction = 'close_button';
      driverObj.destroy();
    },
    onDestroyed: () => {
      const endedAt = Date.now();
      let endStatus = 'dismissed';
      if (completionStatus === 'continue_to_templates_list') {
        endStatus = 'in_progress';
        writeTourState(
          vm.$store,
          {
            dismissed: false,
            status: 'in_progress',
            completedAt: currentState?.completedAt || null,
            dismissedAt: currentState?.dismissedAt,
            inProgressAutoReshowCount,
          },
          storageKeyOverride
        );
      } else {
        endStatus =
          completionStatus === 'completed' || wasPreviouslyCompleted
            ? 'completed'
            : completionStatus === 'passive_close'
            ? currentState?.status || 'in_progress'
            : 'dismissed';

        writeTourState(
          vm.$store,
          {
            dismissed: endStatus === 'dismissed',
            status: endStatus,
            completedAt:
              endStatus === 'completed'
                ? currentState?.completedAt || endedAt
                : currentState?.completedAt || null,
            dismissedAt:
              endStatus === 'dismissed'
                ? endedAt
                : currentState?.dismissedAt,
            inProgressAutoReshowCount:
              endStatus === 'completed'
                ? 0
                : inProgressAutoReshowCount,
          },
          storageKeyOverride
        );
      }

      activeDriver = null;
      activeDriverOwnerVm = null;
      removeInvoiceListStatusFiltersTourTarget();
      removeTemplateSaveButtonTourTarget();
      unlockTourScroll();

      track(vm, 'samples_register_guided_tour_end', {
        tour_flow: tourFlow,
        end_status: endStatus,
        completion_action: completionAction || undefined,
        start_mode: isAutoStart ? 'auto' : 'manual',
        auto_show_count: autoShownCount,
        step_count: steps.length,
      });

      if (completionStatus === 'completed') {
        track(vm, 'samples_register_guided_tour_finish', {
          tour_flow: tourFlow,
          engaged: hasTrackedEngagement,
          auto_show_count: autoShownCount,
        });
        return;
      }

      if (
        completionAction === 'skip_button' ||
        completionAction === 'close_button' ||
        completionAction === 'overlay_click'
      ) {
        track(vm, 'samples_register_guided_tour_dismiss', {
          tour_flow: tourFlow,
          dismiss_action: completionAction,
          engaged: hasTrackedEngagement,
          end_status: endStatus,
          auto_show_count: autoShownCount,
        });
      }
    },
    steps: [],
  });

  activeDriver = driverObj;
  activeDriverOwnerVm = vm;
  driverObj.setSteps(steps);

  track(vm, 'samples_register_guided_tour_start', {
    tour_flow: tourFlow,
    start_mode: isAutoStart ? 'auto' : 'manual',
    auto_show_count: autoShownCount,
    step_count: steps.length,
  });

  settleUi(vm, 120).then(() => {
    if (activeDriver !== driverObj) return;
    lockTourScroll();
    driverObj.drive();
  });

  return driverObj;
}

export function startSamplesRegisterDashboardIntroTour(
  vm,
  { force = false } = {}
) {
  if (
    !vm ||
    !vm.$store ||
    !shouldAutoStartSamplesRegisterGuidedTour(vm.$store, { force })
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    return activeDriver;
  }

  let completionStatus = null;
  let completionAction = null;
  let hasTrackedEngagement = false;
  const tourFlow = 'dashboard_intro';
  const steps = buildDashboardIntroSteps(vm, {
    onContinueToForm: async (driverObj) => {
      markPendingSamplesRegisterTourStart();
      completionStatus = 'continue_to_form';
      completionAction = 'continue_button';

      const registerButton = getVisibleElement(
        DASHBOARD_REGISTER_BUTTON_SELECTOR
      );
      if (registerButton) {
        registerButton.click();
      } else {
        vm.$f7router?.navigate(
          {
            name: 'SamplesRegister',
          },
          {
            animate: true,
            transition: 'f7-fade',
          }
        );
      }

      if (driverObj.isActive()) {
        driverObj.destroy();
      }
    },
  });
  if (!steps.length) return null;

  const currentState = readTourState(vm.$store);
  const wasPreviouslyCompleted = hasCompletedTour(currentState);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const isInProgressAutoStart =
    isAutoStart && hasInProgressTour(currentState);
  const autoShownCount = isAutoStart
    ? (currentState?.autoShownCount || 0) + 1
    : currentState?.autoShownCount || 0;
  const inProgressAutoReshowCount = isInProgressAutoStart
    ? (currentState?.inProgressAutoReshowCount || 0) + 1
    : currentState?.inProgressAutoReshowCount || 0;

  writeTourState(vm.$store, {
    dismissed: false,
    status: wasPreviouslyCompleted ? 'completed' : 'in_progress',
    lastShownAt: startedAt,
    ...(isAutoStart
      ? {
          autoShownCount,
          inProgressAutoReshowCount,
          lastAutoShownAt: startedAt,
        }
      : {
          lastManualShownAt: startedAt,
        }),
  });

  const nextBtnText = vm.$t('next');
  const prevBtnText = vm.$t('agroIA.tour.previous');
  const doneBtnText = nextBtnText;
  const skipBtnText = vm.$t('agroIA.tour.skip');

  const driverObj = driver({
    overlayClickBehavior: () => {
      return false;
    },
    stagePadding: 4,
    stageRadius: 10,
    showProgress: false,
    nextBtnText,
    prevBtnText,
    doneBtnText,
    onPopoverRender: (popover) => {
      const step = driverObj.getActiveStep();
      const isDashboardIntro = Boolean(step?.isDashboardIntro);
      enhancePopover(popover, {
        title: popover?.title?.textContent || '',
        step,
        vm,
      });
      if (popover?.wrapper) {
        popover.wrapper.classList.toggle(
          TOUR_DASHBOARD_POPOVER_CLASS,
          isDashboardIntro
        );
      }
      renderSkipButton(popover, skipBtnText, () => {
        completionStatus = 'dismissed';
        completionAction = 'skip_button';
        driverObj.destroy();
      });
    },
    onNextClick: async () => {
      const activeIndex = driverObj.getActiveIndex();
      if (!hasTrackedEngagement && activeIndex === 0) {
        hasTrackedEngagement = true;
        track(vm, 'samples_register_guided_tour_engaged', {
          tour_flow: tourFlow,
          auto_show_count: autoShownCount,
        });
      }

      const step = driverObj.getActiveStep();

      if (step && typeof step.onNext === 'function') {
        try {
          await step.onNext(driverObj);
        } catch (error) {
          // No-op
        }
        return;
      }

      if (driverObj.hasNextStep()) {
        driverObj.moveNext();
        return;
      }

      completionStatus = 'completed';
      completionAction = 'finish_button';
      driverObj.destroy();
    },
    onPrevClick: () => {
      const step = driverObj.getActiveStep();
      if (step && step.disablePrevious) {
        return;
      }

      if (driverObj.hasPreviousStep()) {
        driverObj.movePrevious();
      }
    },
    onCloseClick: () => {
      completionStatus = 'dismissed';
      completionAction = 'close_button';
      driverObj.destroy();
    },
    onDestroyed: () => {
      const endedAt = Date.now();
      let endStatus = 'dismissed';
      if (completionStatus === 'continue_to_form') {
        endStatus = 'in_progress';
        writeTourState(vm.$store, {
          dismissed: false,
          status: 'in_progress',
          completedAt: currentState?.completedAt || null,
          dismissedAt: currentState?.dismissedAt,
          inProgressAutoReshowCount,
        });
      } else {
        endStatus =
          completionStatus === 'completed' || wasPreviouslyCompleted
            ? 'completed'
            : completionStatus === 'passive_close'
            ? currentState?.status || 'in_progress'
            : 'dismissed';

        writeTourState(vm.$store, {
          dismissed: endStatus === 'dismissed',
          status: endStatus,
          completedAt:
            endStatus === 'completed'
              ? currentState?.completedAt || endedAt
              : currentState?.completedAt || null,
          dismissedAt:
            endStatus === 'dismissed'
              ? endedAt
              : currentState?.dismissedAt,
          inProgressAutoReshowCount:
            endStatus === 'completed' ? 0 : inProgressAutoReshowCount,
        });
      }

      activeDriver = null;
      activeDriverOwnerVm = null;
      removeInvoiceListStatusFiltersTourTarget();
      removeTemplateSaveButtonTourTarget();
      unlockTourScroll();

      track(vm, 'samples_register_guided_tour_end', {
        tour_flow: tourFlow,
        end_status: endStatus,
        completion_action: completionAction || undefined,
        start_mode: isAutoStart ? 'auto' : 'manual',
        auto_show_count: autoShownCount,
        step_count: steps.length,
      });

      if (completionStatus === 'completed') {
        track(vm, 'samples_register_guided_tour_finish', {
          tour_flow: tourFlow,
          engaged: hasTrackedEngagement,
          auto_show_count: autoShownCount,
        });
        return;
      }

      if (
        completionAction === 'skip_button' ||
        completionAction === 'close_button' ||
        completionAction === 'overlay_click'
      ) {
        track(vm, 'samples_register_guided_tour_dismiss', {
          tour_flow: tourFlow,
          dismiss_action: completionAction,
          engaged: hasTrackedEngagement,
          end_status: endStatus,
          auto_show_count: autoShownCount,
        });
      }
    },
    steps: [],
  });

  activeDriver = driverObj;
  activeDriverOwnerVm = vm;
  driverObj.setSteps(steps);

  track(vm, 'samples_register_guided_tour_start', {
    tour_flow: tourFlow,
    start_mode: isAutoStart ? 'auto' : 'manual',
    auto_show_count: autoShownCount,
    step_count: steps.length,
  });

  settleUi(vm, 120).then(() => {
    if (activeDriver !== driverObj) return;
    lockTourScroll();
    driverObj.drive();
  });

  return driverObj;
}

export function startSamplesRegisterGuidedTour(
  vm,
  { force = false } = {}
) {
  if (
    !vm ||
    !vm.$store ||
    !shouldAutoStartSamplesRegisterGuidedTour(vm.$store, { force })
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    return activeDriver;
  }

  let completionStatus = null;
  let completionAction = null;
  let hasTrackedEngagement = false;
  const tourFlow = 'register_form';
  const steps = buildFormSteps(vm, {
    onContinueToInvoiceList: async (driverObj) => {
      markPendingSamplesRegisterInvoiceListTourStart();
      completionStatus = 'continue_to_invoice_list';
      completionAction = 'continue_button';

      vm.$f7router?.navigate(
        {
          name: 'SamplesList',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );

      if (driverObj.isActive()) {
        driverObj.destroy();
      }
    },
  });
  if (!steps.length) return null;

  const currentState = readTourState(vm.$store);
  const wasPreviouslyCompleted = hasCompletedTour(currentState);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const isInProgressAutoStart =
    isAutoStart && hasInProgressTour(currentState);
  const autoShownCount = isAutoStart
    ? (currentState?.autoShownCount || 0) + 1
    : currentState?.autoShownCount || 0;
  const inProgressAutoReshowCount = isInProgressAutoStart
    ? (currentState?.inProgressAutoReshowCount || 0) + 1
    : currentState?.inProgressAutoReshowCount || 0;

  writeTourState(vm.$store, {
    dismissed: false,
    status: wasPreviouslyCompleted ? 'completed' : 'in_progress',
    lastShownAt: startedAt,
    ...(isAutoStart
      ? {
          autoShownCount,
          inProgressAutoReshowCount,
          lastAutoShownAt: startedAt,
        }
      : {
          lastManualShownAt: startedAt,
        }),
  });

  const nextBtnText = vm.$t('next');
  const prevBtnText = vm.$t('agroIA.tour.previous');
  const doneBtnText = nextBtnText;
  const skipBtnText = vm.$t('agroIA.tour.skip');

  const driverObj = driver({
    overlayClickBehavior: () => {
      return false;
    },
    stagePadding: 4,
    stageRadius: 10,
    showProgress: false,
    nextBtnText,
    prevBtnText,
    doneBtnText,
    onPopoverRender: (popover) => {
      const step = driverObj.getActiveStep();
      enhancePopover(popover, {
        title: popover?.title?.textContent || '',
        step,
        vm,
      });
      renderSkipButton(popover, skipBtnText, () => {
        completionStatus = 'dismissed';
        completionAction = 'skip_button';
        driverObj.destroy();
      });

      clearAutoAdvanceTimeout();
      if (step?.autoAdvanceMs > 0) {
        const stepRef = step;
        autoAdvanceTimeoutId = setTimeout(async () => {
          if (!driverObj.isActive()) return;
          if (driverObj.getActiveStep() !== stepRef) return;
          try {
            if (typeof stepRef.onAutoAdvance === 'function') {
              await stepRef.onAutoAdvance(driverObj);
              return;
            }
            if (driverObj.hasNextStep()) {
              driverObj.moveNext();
              return;
            }
            completionStatus = 'completed';
            completionAction = 'auto_advance';
            driverObj.destroy();
          } catch (error) {
            if (driverObj.isActive() && driverObj.hasNextStep()) {
              driverObj.moveNext();
            }
          }
        }, step.autoAdvanceMs);
      }
    },
    onNextClick: async () => {
      clearAutoAdvanceTimeout();
      const activeIndex = driverObj.getActiveIndex();
      if (!hasTrackedEngagement && activeIndex === 0) {
        hasTrackedEngagement = true;
        track(vm, 'samples_register_guided_tour_engaged', {
          tour_flow: tourFlow,
          auto_show_count: autoShownCount,
        });
      }

      const step = driverObj.getActiveStep();

      if (step && typeof step.onNext === 'function') {
        try {
          await step.onNext(driverObj);
        } catch (error) {
          // No-op
        }
        return;
      }

      if (driverObj.hasNextStep()) {
        driverObj.moveNext();
        return;
      }

      completionStatus = 'completed';
      completionAction = 'finish_button';
      driverObj.destroy();
    },
    onPrevClick: () => {
      clearAutoAdvanceTimeout();
      const step = driverObj.getActiveStep();
      if (step && step.disablePrevious) {
        return;
      }

      if (driverObj.hasPreviousStep()) {
        driverObj.movePrevious();
      }
    },
    onCloseClick: () => {
      completionStatus = 'dismissed';
      completionAction = 'close_button';
      driverObj.destroy();
    },
    onDestroyed: () => {
      clearAutoAdvanceTimeout();
      const endedAt = Date.now();
      let endStatus = 'dismissed';
      if (
        completionStatus === 'continue_to_invoice_list'
      ) {
        endStatus = 'in_progress';
        writeTourState(vm.$store, {
          dismissed: false,
          status: 'in_progress',
          completedAt: currentState?.completedAt || null,
          dismissedAt: currentState?.dismissedAt,
          inProgressAutoReshowCount,
        });
      } else {
        endStatus =
          completionStatus === 'completed' || wasPreviouslyCompleted
            ? 'completed'
            : completionStatus === 'passive_close'
            ? currentState?.status || 'in_progress'
            : 'dismissed';

        writeTourState(vm.$store, {
          dismissed: endStatus === 'dismissed',
          status: endStatus,
          completedAt:
            endStatus === 'completed'
              ? currentState?.completedAt || endedAt
              : currentState?.completedAt || null,
          dismissedAt:
            endStatus === 'dismissed'
              ? endedAt
              : currentState?.dismissedAt,
          inProgressAutoReshowCount:
            endStatus === 'completed' ? 0 : inProgressAutoReshowCount,
        });
      }

      vm.hideTourStandardSavePopup?.();
      vm.closeTourVisibleFieldsPopup?.();
      vm.hideTourFinalConfirmationPopup?.();

      activeDriver = null;
      activeDriverOwnerVm = null;
      removeInvoiceListStatusFiltersTourTarget();
      removeTemplateSaveButtonTourTarget();
      unlockTourScroll();

      track(vm, 'samples_register_guided_tour_end', {
        tour_flow: tourFlow,
        end_status: endStatus,
        completion_action: completionAction || undefined,
        start_mode: isAutoStart ? 'auto' : 'manual',
        auto_show_count: autoShownCount,
        step_count: steps.length,
      });

      if (completionStatus === 'completed') {
        track(vm, 'samples_register_guided_tour_finish', {
          tour_flow: tourFlow,
          engaged: hasTrackedEngagement,
          auto_show_count: autoShownCount,
        });
        return;
      }

      if (
        completionAction === 'skip_button' ||
        completionAction === 'close_button' ||
        completionAction === 'overlay_click'
      ) {
        track(vm, 'samples_register_guided_tour_dismiss', {
          tour_flow: tourFlow,
          dismiss_action: completionAction,
          engaged: hasTrackedEngagement,
          end_status: endStatus,
          auto_show_count: autoShownCount,
        });
      }
    },
    steps: [],
  });

  activeDriver = driverObj;
  activeDriverOwnerVm = vm;
  driverObj.setSteps(steps);

  track(vm, 'samples_register_guided_tour_start', {
    tour_flow: tourFlow,
    start_mode: isAutoStart ? 'auto' : 'manual',
    auto_show_count: autoShownCount,
    step_count: steps.length,
  });

  settleUi(vm, 120).then(async () => {
    if (activeDriver !== driverObj) return;
    await vm.prepareSamplesRegisterTourStart?.();
    await settleUi(vm, 120);
    lockTourScroll();
    driverObj.drive();
  });

  return driverObj;
}

export function startSamplesRegisterTemplateResumeGuidedTour(
  vm,
  { force = false } = {}
) {
  if (
    !vm ||
    !vm.$store ||
    !shouldAutoStartSamplesRegisterGuidedTour(vm.$store, { force })
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    return activeDriver;
  }

  let completionStatus = null;
  let completionAction = null;
  let hasTrackedEngagement = false;
  const tourFlow = 'register_form_template_resume';
  const steps = buildFormTemplateResumeSteps(vm, {
    onContinueToInvoiceList: async (driverObj) => {
      markPendingSamplesRegisterInvoiceListTourStart();
      completionStatus = 'continue_to_invoice_list';
      completionAction = 'continue_button';

      vm.$f7router?.navigate(
        {
          name: 'SamplesList',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );

      if (driverObj.isActive()) {
        driverObj.destroy();
      }
    },
  });
  if (!steps.length) return null;

  const currentState = readTourState(vm.$store);
  const wasPreviouslyCompleted = hasCompletedTour(currentState);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const isInProgressAutoStart =
    isAutoStart && hasInProgressTour(currentState);
  const autoShownCount = isAutoStart
    ? (currentState?.autoShownCount || 0) + 1
    : currentState?.autoShownCount || 0;
  const inProgressAutoReshowCount = isInProgressAutoStart
    ? (currentState?.inProgressAutoReshowCount || 0) + 1
    : currentState?.inProgressAutoReshowCount || 0;

  writeTourState(vm.$store, {
    dismissed: false,
    status: wasPreviouslyCompleted ? 'completed' : 'in_progress',
    lastShownAt: startedAt,
    ...(isAutoStart
      ? {
          autoShownCount,
          inProgressAutoReshowCount,
          lastAutoShownAt: startedAt,
        }
      : {
          lastManualShownAt: startedAt,
        }),
  });

  const nextBtnText = vm.$t('next');
  const prevBtnText = vm.$t('agroIA.tour.previous');
  const doneBtnText = nextBtnText;
  const skipBtnText = vm.$t('agroIA.tour.skip');

  const driverObj = driver({
    overlayClickBehavior: () => {
      return false;
    },
    stagePadding: 4,
    stageRadius: 10,
    showProgress: false,
    nextBtnText,
    prevBtnText,
    doneBtnText,
    onPopoverRender: (popover) => {
      const step = driverObj.getActiveStep();
      enhancePopover(popover, {
        title: popover?.title?.textContent || '',
        step,
        vm,
      });
      renderSkipButton(popover, skipBtnText, () => {
        completionStatus = 'dismissed';
        completionAction = 'skip_button';
        driverObj.destroy();
      });
    },
    onNextClick: async () => {
      clearAutoAdvanceTimeout();
      const activeIndex = driverObj.getActiveIndex();
      if (!hasTrackedEngagement && activeIndex === 0) {
        hasTrackedEngagement = true;
        track(vm, 'samples_register_guided_tour_engaged', {
          tour_flow: tourFlow,
          auto_show_count: autoShownCount,
        });
      }

      const step = driverObj.getActiveStep();

      if (step && typeof step.onNext === 'function') {
        try {
          await step.onNext(driverObj);
        } catch (error) {
          // No-op
        }
        return;
      }

      if (driverObj.hasNextStep()) {
        driverObj.moveNext();
        return;
      }

      completionStatus = 'completed';
      completionAction = 'finish_button';
      driverObj.destroy();
    },
    onPrevClick: () => {
      clearAutoAdvanceTimeout();
      const step = driverObj.getActiveStep();
      if (step && step.disablePrevious) {
        return;
      }

      if (driverObj.hasPreviousStep()) {
        driverObj.movePrevious();
      }
    },
    onCloseClick: () => {
      completionStatus = 'dismissed';
      completionAction = 'close_button';
      driverObj.destroy();
    },
    onDestroyed: () => {
      clearAutoAdvanceTimeout();
      const endedAt = Date.now();
      let endStatus = 'dismissed';
      if (completionStatus === 'continue_to_invoice_list') {
        endStatus = 'in_progress';
        writeTourState(vm.$store, {
          dismissed: false,
          status: 'in_progress',
          completedAt: currentState?.completedAt || null,
          dismissedAt: currentState?.dismissedAt,
          inProgressAutoReshowCount,
        });
      } else {
        endStatus =
          completionStatus === 'completed' || wasPreviouslyCompleted
            ? 'completed'
            : completionStatus === 'passive_close'
            ? currentState?.status || 'in_progress'
            : 'dismissed';

        writeTourState(vm.$store, {
          dismissed: endStatus === 'dismissed',
          status: endStatus,
          completedAt:
            endStatus === 'completed'
              ? currentState?.completedAt || endedAt
              : currentState?.completedAt || null,
          dismissedAt:
            endStatus === 'dismissed'
              ? endedAt
              : currentState?.dismissedAt,
          inProgressAutoReshowCount:
            endStatus === 'completed' ? 0 : inProgressAutoReshowCount,
        });
      }

      vm.hideTourStandardSavePopup?.();
      vm.closeTourVisibleFieldsPopup?.();
      vm.hideTourFinalConfirmationPopup?.();

      activeDriver = null;
      activeDriverOwnerVm = null;
      removeInvoiceListStatusFiltersTourTarget();
      removeTemplateSaveButtonTourTarget();
      unlockTourScroll();

      track(vm, 'samples_register_guided_tour_end', {
        tour_flow: tourFlow,
        end_status: endStatus,
        completion_action: completionAction || undefined,
        start_mode: isAutoStart ? 'auto' : 'manual',
        auto_show_count: autoShownCount,
        step_count: steps.length,
      });

      if (completionStatus === 'completed') {
        track(vm, 'samples_register_guided_tour_finish', {
          tour_flow: tourFlow,
          engaged: hasTrackedEngagement,
          auto_show_count: autoShownCount,
        });
        return;
      }

      if (
        completionAction === 'skip_button' ||
        completionAction === 'close_button' ||
        completionAction === 'overlay_click'
      ) {
        track(vm, 'samples_register_guided_tour_dismiss', {
          tour_flow: tourFlow,
          dismiss_action: completionAction,
          engaged: hasTrackedEngagement,
          end_status: endStatus,
          auto_show_count: autoShownCount,
        });
      }
    },
    steps: [],
  });

  activeDriver = driverObj;
  activeDriverOwnerVm = vm;
  driverObj.setSteps(steps);

  track(vm, 'samples_register_guided_tour_start', {
    tour_flow: tourFlow,
    start_mode: isAutoStart ? 'auto' : 'manual',
    auto_show_count: autoShownCount,
    step_count: steps.length,
  });

  settleUi(vm, 120).then(async () => {
    if (activeDriver !== driverObj) return;
    await vm.prepareSamplesRegisterTemplateTourResume?.();
    await settleUi(vm, 120);
    lockTourScroll();
    driverObj.drive();
  });

  return driverObj;
}

export function startSamplesRegisterTemplateCreateGuidedTour(
  vm,
  { force = false } = {}
) {
  if (
    !vm ||
    !vm.$store ||
    !shouldAutoStartSamplesRegisterTemplatesGuidedTour(
      vm.$store,
      { force }
    )
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    if (activeDriverOwnerVm && activeDriverOwnerVm !== vm) {
      activeDriver.destroy();
    } else {
      return activeDriver;
    }
  }

  let completionStatus = null;
  let completionAction = null;
  let hasTrackedEngagement = false;
  const storageKeyOverride =
    SAMPLES_REGISTER_TEMPLATES_GUIDED_TOUR_STORAGE_KEY;
  const tourFlow = 'template_create';
  const steps = buildTemplateCreateSteps(vm, {
    onContinueToTemplatesList: async (driverObj) => {
      completionAction = 'continue_button';
      consumePendingSamplesRegisterTemplatesListTourStart();
      markPendingSamplesRegisterTemplatesListTourStart();
      const openedTemplatesList =
        vm.openTourTemplatesList?.() === true;

      if (openedTemplatesList) {
        completionStatus = 'continue_to_templates_list';
      } else {
        consumePendingSamplesRegisterTemplatesListTourStart();
        completionStatus = 'completed';
      }

      if (driverObj.isActive()) {
        driverObj.destroy();
      }
    },
  });
  if (!steps.length) return null;

  const currentState = readTourState(
    vm.$store,
    storageKeyOverride
  );
  const wasPreviouslyCompleted = hasCompletedTour(currentState);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const isInProgressAutoStart =
    isAutoStart && hasInProgressTour(currentState);
  const autoShownCount = isAutoStart
    ? (currentState?.autoShownCount || 0) + 1
    : currentState?.autoShownCount || 0;
  const inProgressAutoReshowCount = isInProgressAutoStart
    ? (currentState?.inProgressAutoReshowCount || 0) + 1
    : currentState?.inProgressAutoReshowCount || 0;

  writeTourState(
    vm.$store,
    {
      dismissed: false,
      status: wasPreviouslyCompleted ? 'completed' : 'in_progress',
      lastShownAt: startedAt,
      ...(isAutoStart
        ? {
            autoShownCount,
            inProgressAutoReshowCount,
            lastAutoShownAt: startedAt,
          }
        : {
            lastManualShownAt: startedAt,
          }),
    },
    storageKeyOverride
  );

  const nextBtnText = vm.$t('next');
  const prevBtnText = vm.$t('agroIA.tour.previous');
  const doneBtnText = nextBtnText;
  const skipBtnText = vm.$t('agroIA.tour.skip');

  const driverObj = driver({
    overlayClickBehavior: () => {
      return false;
    },
    stagePadding: 4,
    stageRadius: 10,
    showProgress: false,
    nextBtnText,
    prevBtnText,
    doneBtnText,
    onPopoverRender: (popover) => {
      const step = driverObj.getActiveStep();
      enhancePopover(popover, {
        title: popover?.title?.textContent || '',
        step,
        vm,
      });
      renderSkipButton(popover, skipBtnText, () => {
        completionStatus = 'dismissed';
        completionAction = 'skip_button';
        driverObj.destroy();
      });
    },
    onNextClick: async () => {
      clearAutoAdvanceTimeout();
      const activeIndex = driverObj.getActiveIndex();
      if (!hasTrackedEngagement && activeIndex === 0) {
        hasTrackedEngagement = true;
        track(vm, 'samples_register_guided_tour_engaged', {
          tour_flow: tourFlow,
          auto_show_count: autoShownCount,
        });
      }

      const step = driverObj.getActiveStep();

      if (step && typeof step.onNext === 'function') {
        try {
          await step.onNext(driverObj);
        } catch (error) {
          // No-op
        }
        return;
      }

      if (driverObj.hasNextStep()) {
        driverObj.moveNext();
        return;
      }

      completionStatus = 'completed';
      completionAction = 'finish_button';
      driverObj.destroy();
    },
    onPrevClick: () => {
      const step = driverObj.getActiveStep();
      if (step && step.disablePrevious) {
        return;
      }

      if (driverObj.hasPreviousStep()) {
        driverObj.movePrevious();
      }
    },
    onCloseClick: () => {
      completionStatus = 'dismissed';
      completionAction = 'close_button';
      driverObj.destroy();
    },
    onDestroyed: () => {
      const endedAt = Date.now();
      let endStatus = 'dismissed';
      if (completionStatus === 'continue_to_templates_list') {
        endStatus = 'in_progress';
        writeTourState(
          vm.$store,
          {
            dismissed: false,
            status: 'in_progress',
            completedAt: currentState?.completedAt || null,
            dismissedAt: currentState?.dismissedAt,
            inProgressAutoReshowCount,
          },
          storageKeyOverride
        );
      } else {
        endStatus =
          completionStatus === 'completed' || wasPreviouslyCompleted
            ? 'completed'
            : completionStatus === 'passive_close'
            ? currentState?.status || 'in_progress'
            : 'dismissed';

        writeTourState(
          vm.$store,
          {
            dismissed: endStatus === 'dismissed',
            status: endStatus,
            completedAt:
              endStatus === 'completed'
                ? currentState?.completedAt || endedAt
                : currentState?.completedAt || null,
            dismissedAt:
              endStatus === 'dismissed'
                ? endedAt
                : currentState?.dismissedAt,
            inProgressAutoReshowCount:
              endStatus === 'completed'
                ? 0
                : inProgressAutoReshowCount,
          },
          storageKeyOverride
        );
      }

      activeDriver = null;
      activeDriverOwnerVm = null;
      removeInvoiceListStatusFiltersTourTarget();
      removeTemplateSaveButtonTourTarget();
      unlockTourScroll();

      track(vm, 'samples_register_guided_tour_end', {
        tour_flow: tourFlow,
        end_status: endStatus,
        completion_action: completionAction || undefined,
        start_mode: isAutoStart ? 'auto' : 'manual',
        auto_show_count: autoShownCount,
        step_count: steps.length,
      });

      if (completionStatus === 'completed') {
        track(vm, 'samples_register_guided_tour_finish', {
          tour_flow: tourFlow,
          engaged: hasTrackedEngagement,
          auto_show_count: autoShownCount,
        });
        return;
      }

      if (
        completionAction === 'skip_button' ||
        completionAction === 'close_button' ||
        completionAction === 'overlay_click'
      ) {
        track(vm, 'samples_register_guided_tour_dismiss', {
          tour_flow: tourFlow,
          dismiss_action: completionAction,
          engaged: hasTrackedEngagement,
          end_status: endStatus,
          auto_show_count: autoShownCount,
        });
      }
    },
    steps: [],
  });

  activeDriver = driverObj;
  activeDriverOwnerVm = vm;
  driverObj.setSteps(steps);

  track(vm, 'samples_register_guided_tour_start', {
    tour_flow: tourFlow,
    start_mode: isAutoStart ? 'auto' : 'manual',
    auto_show_count: autoShownCount,
    step_count: steps.length,
  });

  settleUi(vm, 120).then(() => {
    if (activeDriver !== driverObj) return;
    lockTourScroll();
    driverObj.drive();
  });

  return driverObj;
}

export function startSamplesRegisterTemplatesListGuidedTour(
  vm,
  { force = false } = {}
) {
  if (
    !vm ||
    !vm.$store ||
    !shouldAutoStartSamplesRegisterTemplatesGuidedTour(
      vm.$store,
      { force }
    )
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    if (activeDriverOwnerVm && activeDriverOwnerVm !== vm) {
      activeDriver.destroy();
    } else {
      return activeDriver;
    }
  }

  let completionStatus = null;
  let completionAction = null;
  let hasTrackedEngagement = false;
  const storageKeyOverride =
    SAMPLES_REGISTER_TEMPLATES_GUIDED_TOUR_STORAGE_KEY;
  const tourFlow = 'templates_list';
  const steps = buildTemplatesListSteps(vm, {
    onContinueToTemplateDetails: async (driverObj) => {
      completionAction = 'continue_button';
      consumePendingSamplesRegisterTemplateDetailsTourStart();
      markPendingSamplesRegisterTemplateDetailsTourStart();
      const detailNavigationResult =
        await vm.openTourTemplateDetails?.();
      const openedTemplateDetails =
        detailNavigationResult === true ||
        detailNavigationResult?.opened === true;

      if (openedTemplateDetails) {
        completionStatus = 'continue_to_template_details';
      } else {
        consumePendingSamplesRegisterTemplateDetailsTourStart();
      }

      if (driverObj.isActive() && openedTemplateDetails) {
        driverObj.destroy();
      }

      return openedTemplateDetails;
    },
    onContinueToRegister: async (driverObj) => {
      completionAction = 'continue_button';
      markPendingSamplesRegisterTemplateResumeTourStart();
      completionStatus = 'continue_to_register_form';
      vm.openTourSamplesRegister?.();

      if (driverObj.isActive()) {
        driverObj.destroy();
      }
    },
  });
  if (!steps.length) return null;

  const currentState = readTourState(
    vm.$store,
    storageKeyOverride
  );
  const wasPreviouslyCompleted = hasCompletedTour(currentState);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const isInProgressAutoStart =
    isAutoStart && hasInProgressTour(currentState);
  const autoShownCount = isAutoStart
    ? (currentState?.autoShownCount || 0) + 1
    : currentState?.autoShownCount || 0;
  const inProgressAutoReshowCount = isInProgressAutoStart
    ? (currentState?.inProgressAutoReshowCount || 0) + 1
    : currentState?.inProgressAutoReshowCount || 0;

  writeTourState(
    vm.$store,
    {
      dismissed: false,
      status: wasPreviouslyCompleted ? 'completed' : 'in_progress',
      lastShownAt: startedAt,
      ...(isAutoStart
        ? {
            autoShownCount,
            inProgressAutoReshowCount,
            lastAutoShownAt: startedAt,
          }
        : {
            lastManualShownAt: startedAt,
          }),
    },
    storageKeyOverride
  );

  const nextBtnText = vm.$t('next');
  const prevBtnText = vm.$t('agroIA.tour.previous');
  const doneBtnText = nextBtnText;
  const skipBtnText = vm.$t('agroIA.tour.skip');

  const driverObj = driver({
    overlayClickBehavior: () => {
      return false;
    },
    stagePadding: 4,
    stageRadius: 10,
    showProgress: false,
    nextBtnText,
    prevBtnText,
    doneBtnText,
    onPopoverRender: (popover) => {
      const step = driverObj.getActiveStep();
      enhancePopover(popover, {
        title: popover?.title?.textContent || '',
        step,
        vm,
      });
      renderSkipButton(popover, skipBtnText, () => {
        completionStatus = 'dismissed';
        completionAction = 'skip_button';
        driverObj.destroy();
      });
    },
    onNextClick: async () => {
      clearAutoAdvanceTimeout();
      const activeIndex = driverObj.getActiveIndex();
      if (!hasTrackedEngagement && activeIndex === 0) {
        hasTrackedEngagement = true;
        track(vm, 'samples_register_guided_tour_engaged', {
          tour_flow: tourFlow,
          auto_show_count: autoShownCount,
        });
      }

      const step = driverObj.getActiveStep();

      if (step && typeof step.onNext === 'function') {
        try {
          await step.onNext(driverObj);
        } catch (error) {
          // No-op
        }
        return;
      }

      if (driverObj.hasNextStep()) {
        driverObj.moveNext();
        return;
      }

      completionStatus = 'completed';
      completionAction = 'finish_button';
      driverObj.destroy();
    },
    onPrevClick: () => {
      const step = driverObj.getActiveStep();
      if (step && step.disablePrevious) {
        return;
      }

      if (driverObj.hasPreviousStep()) {
        driverObj.movePrevious();
      }
    },
    onCloseClick: () => {
      completionStatus = 'dismissed';
      completionAction = 'close_button';
      driverObj.destroy();
    },
    onDestroyed: () => {
      const endedAt = Date.now();
      let endStatus = 'dismissed';
      if (
        completionStatus === 'continue_to_template_details' ||
        completionStatus === 'continue_to_register_form'
      ) {
        endStatus = 'in_progress';
        writeTourState(
          vm.$store,
          {
            dismissed: false,
            status: 'in_progress',
            completedAt: currentState?.completedAt || null,
            dismissedAt: currentState?.dismissedAt,
            inProgressAutoReshowCount,
          },
          storageKeyOverride
        );
      } else {
        endStatus =
          completionStatus === 'completed' || wasPreviouslyCompleted
            ? 'completed'
            : completionStatus === 'passive_close'
            ? currentState?.status || 'in_progress'
            : 'dismissed';

        writeTourState(
          vm.$store,
          {
            dismissed: endStatus === 'dismissed',
            status: endStatus,
            completedAt:
              endStatus === 'completed'
                ? currentState?.completedAt || endedAt
                : currentState?.completedAt || null,
            dismissedAt:
              endStatus === 'dismissed'
                ? endedAt
                : currentState?.dismissedAt,
            inProgressAutoReshowCount:
              endStatus === 'completed'
                ? 0
                : inProgressAutoReshowCount,
          },
          storageKeyOverride
        );
      }

      activeDriver = null;
      activeDriverOwnerVm = null;
      removeInvoiceListStatusFiltersTourTarget();
      removeTemplateSaveButtonTourTarget();
      unlockTourScroll();

      track(vm, 'samples_register_guided_tour_end', {
        tour_flow: tourFlow,
        end_status: endStatus,
        completion_action: completionAction || undefined,
        start_mode: isAutoStart ? 'auto' : 'manual',
        auto_show_count: autoShownCount,
        step_count: steps.length,
      });

      if (completionStatus === 'completed') {
        track(vm, 'samples_register_guided_tour_finish', {
          tour_flow: tourFlow,
          engaged: hasTrackedEngagement,
          auto_show_count: autoShownCount,
        });
        return;
      }

      if (
        completionAction === 'skip_button' ||
        completionAction === 'close_button' ||
        completionAction === 'overlay_click'
      ) {
        track(vm, 'samples_register_guided_tour_dismiss', {
          tour_flow: tourFlow,
          dismiss_action: completionAction,
          engaged: hasTrackedEngagement,
          end_status: endStatus,
          auto_show_count: autoShownCount,
        });
      }
    },
    steps: [],
  });

  activeDriver = driverObj;
  activeDriverOwnerVm = vm;
  driverObj.setSteps(steps);

  track(vm, 'samples_register_guided_tour_start', {
    tour_flow: tourFlow,
    start_mode: isAutoStart ? 'auto' : 'manual',
    auto_show_count: autoShownCount,
    step_count: steps.length,
  });

  settleUi(vm, 120).then(() => {
    if (activeDriver !== driverObj) return;
    lockTourScroll();
    driverObj.drive();
  });

  return driverObj;
}

export function startSamplesRegisterTemplateDetailsGuidedTour(
  vm,
  { force = false } = {}
) {
  if (
    !vm ||
    !vm.$store ||
    !shouldAutoStartSamplesRegisterTemplatesGuidedTour(
      vm.$store,
      { force }
    )
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    if (activeDriverOwnerVm && activeDriverOwnerVm !== vm) {
      activeDriver.destroy();
    } else {
      return activeDriver;
    }
  }

  let completionStatus = null;
  let completionAction = null;
  let hasTrackedEngagement = false;
  const storageKeyOverride =
    SAMPLES_REGISTER_TEMPLATES_GUIDED_TOUR_STORAGE_KEY;
  const tourFlow = 'template_details';
  const steps = buildTemplateDetailsSteps(vm, {
    onContinueToRegister: async (driverObj) => {
      completionAction = 'continue_button';
      markPendingSamplesRegisterTemplateResumeTourStart();
      completionStatus = 'continue_to_register_form';
      vm.openTourSamplesRegister?.();

      if (driverObj.isActive()) {
        driverObj.destroy();
      }
    },
  });
  if (!steps.length) return null;

  const currentState = readTourState(
    vm.$store,
    storageKeyOverride
  );
  const wasPreviouslyCompleted = hasCompletedTour(currentState);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const isInProgressAutoStart =
    isAutoStart && hasInProgressTour(currentState);
  const autoShownCount = isAutoStart
    ? (currentState?.autoShownCount || 0) + 1
    : currentState?.autoShownCount || 0;
  const inProgressAutoReshowCount = isInProgressAutoStart
    ? (currentState?.inProgressAutoReshowCount || 0) + 1
    : currentState?.inProgressAutoReshowCount || 0;

  writeTourState(
    vm.$store,
    {
      dismissed: false,
      status: wasPreviouslyCompleted ? 'completed' : 'in_progress',
      lastShownAt: startedAt,
      ...(isAutoStart
        ? {
            autoShownCount,
            inProgressAutoReshowCount,
            lastAutoShownAt: startedAt,
          }
        : {
            lastManualShownAt: startedAt,
          }),
    },
    storageKeyOverride
  );

  const nextBtnText = vm.$t('next');
  const prevBtnText = vm.$t('agroIA.tour.previous');
  const doneBtnText = nextBtnText;
  const skipBtnText = vm.$t('agroIA.tour.skip');

  const driverObj = driver({
    overlayClickBehavior: () => {
      return false;
    },
    stagePadding: 4,
    stageRadius: 10,
    showProgress: false,
    nextBtnText,
    prevBtnText,
    doneBtnText,
    onPopoverRender: (popover) => {
      const step = driverObj.getActiveStep();
      enhancePopover(popover, {
        title: popover?.title?.textContent || '',
        step,
        vm,
      });
      renderSkipButton(popover, skipBtnText, () => {
        completionStatus = 'dismissed';
        completionAction = 'skip_button';
        driverObj.destroy();
      });
    },
    onNextClick: async () => {
      clearAutoAdvanceTimeout();
      const activeIndex = driverObj.getActiveIndex();
      if (!hasTrackedEngagement && activeIndex === 0) {
        hasTrackedEngagement = true;
        track(vm, 'samples_register_guided_tour_engaged', {
          tour_flow: tourFlow,
          auto_show_count: autoShownCount,
        });
      }

      const step = driverObj.getActiveStep();

      if (step && typeof step.onNext === 'function') {
        try {
          await step.onNext(driverObj);
        } catch (error) {
          // No-op
        }
        return;
      }

      if (driverObj.hasNextStep()) {
        driverObj.moveNext();
        return;
      }

      completionStatus = 'completed';
      completionAction = 'finish_button';
      driverObj.destroy();
    },
    onPrevClick: () => {
      const step = driverObj.getActiveStep();
      if (step && step.disablePrevious) {
        return;
      }

      if (driverObj.hasPreviousStep()) {
        driverObj.movePrevious();
      }
    },
    onCloseClick: () => {
      completionStatus = 'dismissed';
      completionAction = 'close_button';
      driverObj.destroy();
    },
    onDestroyed: () => {
      const endedAt = Date.now();
      let endStatus = 'dismissed';
      if (completionStatus === 'continue_to_register_form') {
        endStatus = 'in_progress';
        writeTourState(
          vm.$store,
          {
            dismissed: false,
            status: 'in_progress',
            completedAt: currentState?.completedAt || null,
            dismissedAt: currentState?.dismissedAt,
            inProgressAutoReshowCount,
          },
          storageKeyOverride
        );
      } else {
        endStatus =
          completionStatus === 'completed' || wasPreviouslyCompleted
            ? 'completed'
            : completionStatus === 'passive_close'
            ? currentState?.status || 'in_progress'
            : 'dismissed';

        writeTourState(
          vm.$store,
          {
            dismissed: endStatus === 'dismissed',
            status: endStatus,
            completedAt:
              endStatus === 'completed'
                ? currentState?.completedAt || endedAt
                : currentState?.completedAt || null,
            dismissedAt:
              endStatus === 'dismissed'
                ? endedAt
                : currentState?.dismissedAt,
            inProgressAutoReshowCount:
              endStatus === 'completed'
                ? 0
                : inProgressAutoReshowCount,
          },
          storageKeyOverride
        );
      }

      activeDriver = null;
      activeDriverOwnerVm = null;
      removeInvoiceListStatusFiltersTourTarget();
      removeTemplateSaveButtonTourTarget();
      unlockTourScroll();

      track(vm, 'samples_register_guided_tour_end', {
        tour_flow: tourFlow,
        end_status: endStatus,
        completion_action: completionAction || undefined,
        start_mode: isAutoStart ? 'auto' : 'manual',
        auto_show_count: autoShownCount,
        step_count: steps.length,
      });

      if (completionStatus === 'completed') {
        track(vm, 'samples_register_guided_tour_finish', {
          tour_flow: tourFlow,
          engaged: hasTrackedEngagement,
          auto_show_count: autoShownCount,
        });
        return;
      }

      if (
        completionAction === 'skip_button' ||
        completionAction === 'close_button' ||
        completionAction === 'overlay_click'
      ) {
        track(vm, 'samples_register_guided_tour_dismiss', {
          tour_flow: tourFlow,
          dismiss_action: completionAction,
          engaged: hasTrackedEngagement,
          end_status: endStatus,
          auto_show_count: autoShownCount,
        });
      }
    },
    steps: [],
  });

  activeDriver = driverObj;
  activeDriverOwnerVm = vm;
  driverObj.setSteps(steps);

  track(vm, 'samples_register_guided_tour_start', {
    tour_flow: tourFlow,
    start_mode: isAutoStart ? 'auto' : 'manual',
    auto_show_count: autoShownCount,
    step_count: steps.length,
  });

  settleUi(vm, 120).then(() => {
    if (activeDriver !== driverObj) return;
    lockTourScroll();
    driverObj.drive();
  });

  return driverObj;
}

export function startSamplesRegisterInvoiceListGuidedTour(
  vm,
  { force = false } = {}
) {
  if (
    !vm ||
    !vm.$store ||
    !shouldAutoStartSamplesRegisterGuidedTour(vm.$store, { force })
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    return activeDriver;
  }

  let completionStatus = null;
  let completionAction = null;
  let hasTrackedEngagement = false;
  const tourFlow = 'invoice_list';
  const steps = buildInvoiceListSteps(vm, {
    onContinueToDetails: async (driverObj) => {
      completionAction = 'continue_button';
      consumePendingSamplesRegisterInvoiceListDetailsTourStart();
      const detailNavigationResult = await vm.openTourInvoiceDetail?.(
        {
          onBeforeNavigateToInTransitDetails: () => {
            markPendingSamplesRegisterInvoiceListDetailsTourStart();
          },
        }
      );
      const openedDetails =
        detailNavigationResult === true ||
        detailNavigationResult?.opened === true;
      const shouldContinueToInvoiceDetailsTour =
        detailNavigationResult === true ||
        detailNavigationResult?.isInTransit === true;

      if (!openedDetails) {
        completionStatus = 'completed';
      } else if (shouldContinueToInvoiceDetailsTour) {
        completionStatus = 'continue_to_invoice_details';
      } else {
        completionStatus = 'completed';
      }

      if (driverObj.isActive()) {
        driverObj.destroy();
      }
    },
  });
  if (!steps.length) return null;

  const currentState = readTourState(vm.$store);
  const wasPreviouslyCompleted = hasCompletedTour(currentState);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const isInProgressAutoStart =
    isAutoStart && hasInProgressTour(currentState);
  const autoShownCount = isAutoStart
    ? (currentState?.autoShownCount || 0) + 1
    : currentState?.autoShownCount || 0;
  const inProgressAutoReshowCount = isInProgressAutoStart
    ? (currentState?.inProgressAutoReshowCount || 0) + 1
    : currentState?.inProgressAutoReshowCount || 0;

  writeTourState(vm.$store, {
    dismissed: false,
    status: wasPreviouslyCompleted ? 'completed' : 'in_progress',
    lastShownAt: startedAt,
    ...(isAutoStart
      ? {
          autoShownCount,
          inProgressAutoReshowCount,
          lastAutoShownAt: startedAt,
        }
      : {
          lastManualShownAt: startedAt,
        }),
  });

  const nextBtnText = vm.$t('next');
  const prevBtnText = vm.$t('agroIA.tour.previous');
  const doneBtnText = nextBtnText;
  const skipBtnText = vm.$t('agroIA.tour.skip');

  const driverObj = driver({
    overlayClickBehavior: () => {
      return false;
    },
    stagePadding: 4,
    stageRadius: 10,
    showProgress: false,
    nextBtnText,
    prevBtnText,
    doneBtnText,
    onPopoverRender: (popover) => {
      const step = driverObj.getActiveStep();
      enhancePopover(popover, {
        title: popover?.title?.textContent || '',
        step,
        vm,
      });
      renderSkipButton(popover, skipBtnText, () => {
        completionStatus = 'dismissed';
        completionAction = 'skip_button';
        driverObj.destroy();
      });
    },
    onNextClick: async () => {
      clearAutoAdvanceTimeout();
      const activeIndex = driverObj.getActiveIndex();
      if (!hasTrackedEngagement && activeIndex === 0) {
        hasTrackedEngagement = true;
        track(vm, 'samples_register_guided_tour_engaged', {
          tour_flow: tourFlow,
          auto_show_count: autoShownCount,
        });
      }

      const step = driverObj.getActiveStep();

      if (step && typeof step.onNext === 'function') {
        try {
          await step.onNext(driverObj);
        } catch (error) {
          // No-op
        }
        return;
      }

      if (driverObj.hasNextStep()) {
        driverObj.moveNext();
        return;
      }

      completionStatus = 'completed';
      completionAction = 'finish_button';
      driverObj.destroy();
    },
    onPrevClick: () => {
      clearAutoAdvanceTimeout();
      const step = driverObj.getActiveStep();
      if (step && step.disablePrevious) {
        return;
      }

      if (driverObj.hasPreviousStep()) {
        driverObj.movePrevious();
      }
    },
    onCloseClick: () => {
      completionStatus = 'dismissed';
      completionAction = 'close_button';
      driverObj.destroy();
    },
    onDestroyed: () => {
      clearAutoAdvanceTimeout();
      const endedAt = Date.now();
      let endStatus = 'dismissed';
      if (completionStatus === 'continue_to_invoice_details') {
        endStatus = 'in_progress';
        writeTourState(vm.$store, {
          dismissed: false,
          status: 'in_progress',
          completedAt: currentState?.completedAt || null,
          dismissedAt: currentState?.dismissedAt,
          inProgressAutoReshowCount,
        });
      } else {
        endStatus =
          completionStatus === 'completed' || wasPreviouslyCompleted
            ? 'completed'
            : completionStatus === 'passive_close'
            ? currentState?.status || 'in_progress'
            : 'dismissed';

        writeTourState(vm.$store, {
          dismissed: endStatus === 'dismissed',
          status: endStatus,
          completedAt:
            endStatus === 'completed'
              ? currentState?.completedAt || endedAt
              : currentState?.completedAt || null,
          dismissedAt:
            endStatus === 'dismissed'
              ? endedAt
              : currentState?.dismissedAt,
          inProgressAutoReshowCount:
            endStatus === 'completed' ? 0 : inProgressAutoReshowCount,
        });
      }

      activeDriver = null;
      activeDriverOwnerVm = null;
      removeInvoiceListStatusFiltersTourTarget();
      removeTemplateSaveButtonTourTarget();
      unlockTourScroll();

      track(vm, 'samples_register_guided_tour_end', {
        tour_flow: tourFlow,
        end_status: endStatus,
        completion_action: completionAction || undefined,
        start_mode: isAutoStart ? 'auto' : 'manual',
        auto_show_count: autoShownCount,
        step_count: steps.length,
      });

      if (completionStatus === 'completed') {
        track(vm, 'samples_register_guided_tour_finish', {
          tour_flow: tourFlow,
          engaged: hasTrackedEngagement,
          auto_show_count: autoShownCount,
        });
        return;
      }

      if (
        completionAction === 'skip_button' ||
        completionAction === 'close_button' ||
        completionAction === 'overlay_click'
      ) {
        track(vm, 'samples_register_guided_tour_dismiss', {
          tour_flow: tourFlow,
          dismiss_action: completionAction,
          engaged: hasTrackedEngagement,
          end_status: endStatus,
          auto_show_count: autoShownCount,
        });
      }
    },
    steps: [],
  });

  activeDriver = driverObj;
  activeDriverOwnerVm = vm;
  driverObj.setSteps(steps);

  track(vm, 'samples_register_guided_tour_start', {
    tour_flow: tourFlow,
    start_mode: isAutoStart ? 'auto' : 'manual',
    auto_show_count: autoShownCount,
    step_count: steps.length,
  });

  settleUi(vm, 120).then(() => {
    if (activeDriver !== driverObj) return;
    lockTourScroll();
    driverObj.drive();
  });

  return driverObj;
}

export function startSamplesRegisterInvoiceListDetailsGuidedTour(
  vm,
  { force = false } = {}
) {
  if (
    !vm ||
    !vm.$store ||
    !shouldAutoStartSamplesRegisterGuidedTour(vm.$store, { force })
  ) {
    return null;
  }

  if (activeDriver && activeDriver.isActive()) {
    if (activeDriverOwnerVm && activeDriverOwnerVm !== vm) {
      activeDriver.destroy();
    } else {
      return activeDriver;
    }
  }

  let completionStatus = null;
  let completionAction = null;
  let shouldReturnToInvoiceList = false;
  const steps = buildInvoiceListDetailsSteps(vm, {
    onFinish: async (driverObj) => {
      shouldReturnToInvoiceList = true;
      completionStatus = 'completed';
      completionAction = 'finish_button';

      if (driverObj.isActive()) {
        driverObj.destroy();
      }
    },
  });
  if (!steps.length) return null;

  const currentState = readTourState(vm.$store);
  const wasPreviouslyCompleted = hasCompletedTour(currentState);
  const startedAt = Date.now();
  const isAutoStart = !force;
  const isInProgressAutoStart =
    isAutoStart && hasInProgressTour(currentState);
  const autoShownCount = isAutoStart
    ? (currentState?.autoShownCount || 0) + 1
    : currentState?.autoShownCount || 0;
  const inProgressAutoReshowCount = isInProgressAutoStart
    ? (currentState?.inProgressAutoReshowCount || 0) + 1
    : currentState?.inProgressAutoReshowCount || 0;

  writeTourState(vm.$store, {
    dismissed: false,
    status: wasPreviouslyCompleted ? 'completed' : 'in_progress',
    lastShownAt: startedAt,
    ...(isAutoStart
      ? {
          autoShownCount,
          inProgressAutoReshowCount,
          lastAutoShownAt: startedAt,
        }
      : {
          lastManualShownAt: startedAt,
        }),
  });

  let hasTrackedEngagement = false;
  const trackedStepViews = new Set();
  const tourFlow = 'invoice_details';
  const nextBtnText = vm.$t('next');
  const prevBtnText = vm.$t('agroIA.tour.previous');
  const doneBtnText = vm.$t('agroIA.tour.finish');
  const skipBtnText = vm.$t('agroIA.tour.skip');

  const driverObj = driver({
    overlayClickBehavior: () => {
      return false;
    },
    stagePadding: 4,
    stageRadius: 10,
    showProgress: false,
    nextBtnText,
    prevBtnText,
    doneBtnText,
    onPopoverRender: (popover) => {
      const step = driverObj.getActiveStep();
      enhancePopover(popover, {
        title: popover?.title?.textContent || '',
        step,
        vm,
      });
      renderSkipButton(popover, skipBtnText, () => {
        completionStatus = 'dismissed';
        completionAction = 'skip_button';
        driverObj.destroy();
      });

      const stepIndex = driverObj.getActiveIndex();
      if (stepIndex < 0) {
        return;
      }

      const analyticsStepKey =
        step?.analyticsStepKey ||
        `invoice_details_step_${stepIndex + 1}`;
      const stepTrackingKey = `${stepIndex}:${analyticsStepKey}`;

      if (!trackedStepViews.has(stepTrackingKey)) {
        trackedStepViews.add(stepTrackingKey);
        track(vm, 'samples_register_guided_tour_step_view', {
          tour_flow: tourFlow,
          step_index: stepIndex + 1,
          step_key: analyticsStepKey,
          step_count: steps.length,
          auto_show_count: autoShownCount,
        });
      }
    },
    onNextClick: async () => {
      clearAutoAdvanceTimeout();
      const activeIndex = driverObj.getActiveIndex();
      if (!hasTrackedEngagement && activeIndex === 0) {
        hasTrackedEngagement = true;
        track(vm, 'samples_register_guided_tour_engaged', {
          tour_flow: tourFlow,
          auto_show_count: autoShownCount,
        });
      }

      const step = driverObj.getActiveStep();

      if (step && typeof step.onNext === 'function') {
        try {
          await step.onNext(driverObj);
        } catch (error) {
          // No-op
        }
        return;
      }

      if (driverObj.hasNextStep()) {
        driverObj.moveNext();
        return;
      }

      completionStatus = 'completed';
      completionAction = 'finish_button';
      driverObj.destroy();
    },
    onPrevClick: () => {
      clearAutoAdvanceTimeout();
      const step = driverObj.getActiveStep();
      if (step && step.disablePrevious) {
        return;
      }

      if (driverObj.hasPreviousStep()) {
        driverObj.movePrevious();
      }
    },
    onCloseClick: () => {
      completionStatus = 'dismissed';
      completionAction = 'close_button';
      driverObj.destroy();
    },
    onDestroyed: () => {
      clearAutoAdvanceTimeout();
      const endedAt = Date.now();
      const endStatus =
        completionStatus === 'completed' || wasPreviouslyCompleted
          ? 'completed'
          : completionStatus === 'passive_close'
          ? currentState?.status || 'in_progress'
          : 'dismissed';

      writeTourState(vm.$store, {
        dismissed: endStatus === 'dismissed',
        status: endStatus,
        completedAt:
          endStatus === 'completed'
            ? currentState?.completedAt || endedAt
            : currentState?.completedAt || null,
        dismissedAt:
          endStatus === 'dismissed'
            ? endedAt
            : currentState?.dismissedAt,
        inProgressAutoReshowCount:
          endStatus === 'completed' ? 0 : inProgressAutoReshowCount,
      });

      activeDriver = null;
      activeDriverOwnerVm = null;
      removeInvoiceListStatusFiltersTourTarget();
      removeTemplateSaveButtonTourTarget();
      unlockTourScroll();

      track(vm, 'samples_register_guided_tour_end', {
        tour_flow: tourFlow,
        end_status: endStatus,
        completion_action: completionAction || undefined,
        start_mode: isAutoStart ? 'auto' : 'manual',
        auto_show_count: autoShownCount,
        step_count: steps.length,
      });

      if (completionStatus === 'completed') {
        if (shouldReturnToInvoiceList) {
          vm.returnToTourInvoiceList?.();
        }

        track(vm, 'samples_register_guided_tour_finish', {
          tour_flow: tourFlow,
          engaged: hasTrackedEngagement,
          auto_show_count: autoShownCount,
        });
        return;
      }

      if (
        completionAction === 'skip_button' ||
        completionAction === 'close_button' ||
        completionAction === 'overlay_click'
      ) {
        track(vm, 'samples_register_guided_tour_dismiss', {
          tour_flow: tourFlow,
          dismiss_action: completionAction,
          engaged: hasTrackedEngagement,
          end_status: endStatus,
          auto_show_count: autoShownCount,
        });
      }
    },
    steps: [],
  });

  activeDriver = driverObj;
  activeDriverOwnerVm = vm;
  driverObj.setSteps(steps);

  track(vm, 'samples_register_guided_tour_start', {
    tour_flow: tourFlow,
    start_mode: isAutoStart ? 'auto' : 'manual',
    auto_show_count: autoShownCount,
    step_count: steps.length,
  });

  settleUi(vm, 120).then(() => {
    if (activeDriver !== driverObj) return;
    lockTourScroll();
    driverObj.drive();
  });

  return driverObj;
}
